import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ModernButton } from '../ui/ModernButton';
import { FlowingLines } from '../ui/FlowingLines';
import { EligibilityCard } from '../ui/EligibilityCard';
import { dbService, DB_CONFIG, userDisplayName } from '../../services/DatabaseService';
import { sanitizeRichText } from '../../utils/helpers';
import { TrashIcon, ArrowLeftIcon, RefreshIcon, SparklesIcon } from '../ui/Icons';
import { StickyNotePlus, History } from 'lucide-react';
import {
    buildNameIndex,
    findRelatedApplications,
    contactKeysFromAnswers,
    classifyMatch,
    MATCH_LABELS
} from '../../utils/applicantIdentity';
import { getQualificationEmoji, getPointScoreColor, getPointScoreTextColor, getPointScoreEmoji, getRecommendation } from '../../utils/scoreConfig';
import { normalizeQuestionnaireData, computeDisplayLabels } from '../../utils/questionnairePages';
import pb, { requireAuth } from '../../pocketbaseClient';
import { evaluationPipeline } from '../../services/evaluation/EvaluationPipeline';
import { detectRedactablePii, flattenAnswerText } from '../../utils/contactInfo';
import { EvaluationProgress, buildInitialProgress, markStepStart, markStepEnd, markAttempt } from '../ui/EvaluationProgress';
import { subscribeToAttempts } from '../../services/geminiAttemptLog';
import { Pagination } from '../ui/Pagination';
import { paginate, RESULTS_PAGE_SIZE } from '../../utils/pagination';
import { SendAssessmentDialog } from '../jobs/SendAssessmentDialog';
import { inviteService } from '../../services/AssessmentService';
import { inviteActivity, formatSince } from '../../utils/inviteActivity';
import { getUserColor } from '../../utils/userColor';
import fileUploadService from '../../services/FileUploadService';
import toast from 'react-hot-toast';
import { migrateNotes, addNoteEntry, editNoteEntry, deleteNoteEntry, noteByline } from '../../utils/notesFeed';
import { wasQuestionVisible } from '../../utils/conditionalVisibility';
import { collectKnownQuestionIds, recoverOrphanAnswers } from '../../utils/submissionAnswerRecovery';
import { STATUS_CONFIG, getStatusColor, statusChoices, isLegacyStatus } from '../../utils/submissionStatus';

const formatGermanDate = (value) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime())
        ? 'Datum unbekannt'
        : date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Trennt die anderen Bewerbungen derselben Person in frühere und spätere.
// `related` kommt bereits neueste zuerst sortiert an.
const splitByTime = (submission, related) => {
    const current = new Date(submission?.submittedAt).getTime();
    if (Number.isNaN(current)) return { earlier: [], later: related };
    const earlier = related.filter((other) => new Date(other.submittedAt).getTime() < current);
    return { earlier, later: related.filter((other) => !earlier.includes(other)) };
};

// Wiederbewerber-Hinweis in der Ergebnisliste. Die Liste kennt nur Namen —
// die Bestätigung über E-Mail oder Telefon steht in der Detailansicht, weil
// dafür die Antworten nachgeladen werden müssen. Der Klick springt direkt in
// die betreffende ältere Bewerbung.
function RepeatApplicantBadge({ submission, related, onOpen }) {
    if (!related || related.length === 0) return null;

    const { earlier } = splitByTime(submission, related);
    const target = earlier[0] || related[0];
    const year = new Date(target.submittedAt).getFullYear();
    const label = earlier.length > 1
        ? `Schon beworben · ${earlier.length}×`
        : earlier.length === 1
            ? `Schon beworben · ${year}`
            : `Erneut beworben · ${year}`;

    return (
        <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpen(target.id); }}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 rounded-full hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors"
            title={`Gleicher Name wie in einer Bewerbung vom ${formatGermanDate(target.submittedAt)} — klicken zum Öffnen`}
        >
            <History className="w-3.5 h-3.5" />
            {label}
        </button>
    );
}

// Detailansicht: alle weiteren Bewerbungen derselben Person, je Eintrag mit
// der Trefferstufe. Der Namenstreffer steht schon fest; E-Mail und Telefon
// der anderen Bewerbungen werden hier einmal nachgeladen und bestätigen ihn.
function PreviousApplications({ submission, related, onOpen }) {
    const [contactsById, setContactsById] = useState(null);
    const relatedIds = related.map((entry) => entry.id).join(',');

    useEffect(() => {
        if (!relatedIds) { setContactsById({}); return undefined; }
        let cancelled = false;
        setContactsById(null);
        dbService.getSubmissionAnswersByIds(relatedIds.split(','))
            .then((answersById) => {
                if (cancelled) return;
                const map = {};
                Object.entries(answersById).forEach(([id, answers]) => {
                    map[id] = contactKeysFromAnswers(answers);
                });
                setContactsById(map);
            })
            .catch(() => { if (!cancelled) setContactsById({}); });
        return () => { cancelled = true; };
    }, [relatedIds]);

    const ownContacts = useMemo(
        () => contactKeysFromAnswers(submission.answers),
        [submission.answers]
    );

    if (related.length === 0) return null;

    const { earlier } = splitByTime(submission, related);

    return (
        <section className="mb-6 rounded-2xl border border-amber-200 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-900/20 p-4 sm:p-5">
            <h2 className="flex items-center gap-2 text-base sm:text-lg font-bold text-amber-900 dark:text-amber-200">
                <History className="w-5 h-5 shrink-0" />
                {earlier.length > 0
                    ? 'Diese Person hat sich schon einmal beworben'
                    : 'Diese Person hat sich später erneut beworben'}
            </h2>
            <p className="mt-1 text-xs text-amber-800/80 dark:text-amber-300/80">
                Gefunden über den Namen. E-Mail oder Telefonnummer bestätigen den Treffer, sofern sie angegeben wurden.
            </p>
            <ul className="mt-3 divide-y divide-amber-200/70 dark:divide-amber-800/50">
                {related.map((entry) => {
                    const matchLabel = contactsById === null
                        ? 'Wird geprüft …'
                        : MATCH_LABELS[classifyMatch(ownContacts, contactsById[entry.id])];
                    return (
                        <li key={entry.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                            <div className="min-w-0">
                                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 wrap-break-word">
                                    {formatGermanDate(entry.submittedAt)} · {entry.questionnaireTitle}
                                </p>
                                {entry.jobTitle && (
                                    <p className="text-xs text-slate-600 dark:text-slate-300">Stelle: {entry.jobTitle}</p>
                                )}
                                <p className="text-xs text-amber-800 dark:text-amber-300">{matchLabel}</p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <span className={`px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(entry.status || 'Unbearbeitet')}`}>
                                    {entry.status || 'Unbearbeitet'}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => onOpen(entry.id)}
                                    className="px-3 py-1 text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors"
                                >
                                    Öffnen
                                </button>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}

// Subtle marker signalling HR that the named data was removed before the AI
// evaluated it. (HR still sees the real value above; only the AI prompt is
// scrubbed — name- and contact-blind.)
function RedactionMarker({ label }) {
    return (
        <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 mt-1" title="Diese Angabe wurde vor der KI-Bewertung aus dem KI-Prompt entfernt. Die KI bewertet anonymisiert.">
            <span aria-hidden="true">🛡️</span>
            <span>{label} für die KI-Bewertung ausgeblendet</span>
        </div>
    );
}

// Uploaded files are never included in the AI prompt — evaluation/context.js
// filters out file-upload questions — so flag that to HR under the file list.
function FileUploadAiHint() {
    return (
        <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 mt-1" title="Hochgeladene Dateien werden nicht an die KI gesendet und fließen nicht in die KI-Bewertung ein. Nur HR sieht sie hier.">
            <span aria-hidden="true">🛡️</span>
            <span>Dateien werden nicht an die KI gesendet</span>
        </div>
    );
}

// Shows the marker under an answer that contained an email/phone the AI never saw.
function RedactionHint({ answer }) {
    const { email, phone } = detectRedactablePii(flattenAnswerText(answer));
    if (!email && !phone) return null;
    const parts = [];
    if (email) parts.push('E-Mail');
    if (phone) parts.push('Telefonnummer');
    return <RedactionMarker label={parts.join(' & ')} />;
}

// --- Notification Component ---
function Notification({ message, type = 'success', onClose }) {
    useEffect(() => {
        const timer = setTimeout(() => {
            onClose();
        }, 3000); // Auto-hide after 3 seconds

        return () => clearTimeout(timer);
    }, [onClose]);

    const getNotificationStyles = () => {
        if (type === 'error') {
            return 'bg-red-100 border-red-400 text-red-700 dark:bg-red-900 dark:border-red-600 dark:text-red-200';
        }
        return 'bg-green-100 border-green-400 text-green-700 dark:bg-green-900 dark:border-green-600 dark:text-green-200';
    };

    const getIcon = () => {
        if (type === 'error') {
            return (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
            );
        }
        return (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
        );
    };

    return (
        <div className="fixed top-4 right-4 z-50 transform transition-all duration-300 ease-out animate-pulse">
            <div className={`border rounded-lg shadow-lg p-4 flex items-center gap-3 min-w-[200px] ${getNotificationStyles()}`}>
                <div className="shrink-0">
                    {getIcon()}
                </div>
                <div className="flex-1">
                    <p className="text-sm font-medium">{message}</p>
                </div>
                <button
                    onClick={onClose}
                    className="shrink-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
        </div>
    );
}

const resolveEffectiveQuestionType = (questionType, answer, answerType = null, options = []) => {
  if (questionType !== 'image') return questionType;

  const normalizedAnswerType = String(answerType || '').trim();
  if (['text', 'single-choice', 'multiple-choice', 'ranking'].includes(normalizedAnswerType)) {
    return normalizedAnswerType;
  }

  if (Array.isArray(answer)) return 'multiple-choice';

  const normalizedOptions = Array.isArray(options)
    ? options.map((opt) => String(opt || '').trim()).filter(Boolean)
    : [];
  if (typeof answer === 'string' && normalizedOptions.includes(answer.trim())) {
    return 'single-choice';
  }

  return 'text';
};

// Helper function to check if a question is answered (same logic as the form)
const isQuestionAnswered = (questionId, questionType, answers, answerType = null, options = []) => {
  const answer = answers[questionId];
  const effectiveQuestionType = resolveEffectiveQuestionType(questionType, answer, answerType, options);

  if (effectiveQuestionType === 'file-upload') {
    return answer && Array.isArray(answer.fileIds) && answer.fileIds.length > 0;
  }
  if (effectiveQuestionType === 'matrix' || effectiveQuestionType === 'combobox') {
    // "Answered" means at least one row has a selected value. Empty-value rows are unanswered slots.
    return Array.isArray(answer) && answer.some(r => r && r.value);
  }
  if (effectiveQuestionType === 'text' || effectiveQuestionType === 'single-choice') {
    return typeof answer === 'string' && answer.trim() !== '';
  }
  // Eine Rangordnung gilt als beantwortet, sobald eine Option einen Rang hat.
  // Das Formular lässt Bewerber nur die Optionen ordnen, die ihnen etwas
  // bedeuten, und die Anzeige schreibt ohnehin "3 von 5 gewählt" darüber. Die
  // Bedingung auf eine vollständige Reihenfolge versteckte genau diese
  // Antworten hinter "Keine Antwort" — während die Gemini-Analyse direkt
  // darunter sie zitierte, weil sie sie aus denselben Daten liest.
  if (effectiveQuestionType === 'ranking') {
    return Array.isArray(answer) && answer.length > 0;
  }
  if (effectiveQuestionType === 'multiple-choice') {
    return Array.isArray(answer) && answer.length > 0;
  }
  return false;
};

// Helper function to get display text for unanswered questions
const getUnansweredText = (questionType) => {
  return 'Keine Antwort';
};

const getOrderedRankingEntries = (answer = [], options = []) => {
  if (!Array.isArray(answer) || answer.length === 0) return [];
  const normalizedOptions = Array.isArray(options) ? options : [];

  return answer
    .map((raw, rankIndex) => {
      const numericIndex = Number(raw);
      let optionLabel = '';

      if (Number.isInteger(numericIndex) && numericIndex >= 0 && numericIndex < normalizedOptions.length) {
        optionLabel = normalizedOptions[numericIndex];
      } else if (typeof raw === 'string') {
        optionLabel = raw;
      }

      const cleanLabel = String(optionLabel || '').trim();
      if (!cleanLabel) return null;
      return { rank: rankIndex + 1, label: cleanLabel };
    })
    .filter(Boolean);
};

const getDefaultQuestionPointTotals = (pointEvaluation) => {
    const questionScores = Array.isArray(pointEvaluation?.questionScores) ? pointEvaluation.questionScores : [];
    const defaultQuestionScores = questionScores.filter((scoreItem) => String(scoreItem?.questionId || '').startsWith('default_'));
    if (defaultQuestionScores.length === 0) {
        return null;
    }

    const score = defaultQuestionScores.reduce((sum, scoreItem) => sum + (Number(scoreItem?.score) || 0), 0);
    const maxScore = defaultQuestionScores.reduce((sum, scoreItem) => sum + (Number(scoreItem?.maxScore) || 0), 0);
    return {
        score,
        maxScore
    };
};

const getTotalPointTotals = (pointEvaluation) => {
    const totalScore = Number(pointEvaluation?.totalScore);
    const maxScore = Number(pointEvaluation?.maxScore);
    if (Number.isFinite(totalScore) && Number.isFinite(maxScore) && maxScore > 0) {
        return { score: totalScore, maxScore };
    }
    return null;
};

// Strip a leading page number prefix ("2) ", "3 ", "1. ") from a page title for
// a cleaner score-breakdown label. Leaves titles like "24/7 Support" untouched.
const formatPageTitle = (title) => String(title || '').replace(/^\s*\d+\s*[).]\s*|^\s*\d+\s+/, '').trim();

// Group the per-question point scores by the questionnaire's pages, using the
// SAME ordered sections that drive the answer tabs (so the breakdown order
// matches what the reviewer sees below). Each page's subtotal is the sum of its
// questions' scores; the page subtotals add up to the Gesamtscore.
const buildPointPageGroups = (pointEvaluation, orderedSections = []) => {
    const scores = Array.isArray(pointEvaluation?.questionScores) ? pointEvaluation.questionScores : [];
    const byId = new Map(scores.map((qs) => [String(qs.questionId), qs]));
    const questionById = new Map();
    const used = new Set();
    const groups = [];

    orderedSections.forEach((section) => {
        const sectionQuestions = (section.questions || []).filter((q) => q && q.id !== 'system_name');
        sectionQuestions.forEach((q) => questionById.set(String(q.id), q));
        const items = sectionQuestions.map((q) => byId.get(String(q.id))).filter(Boolean);
        items.forEach((it) => used.add(String(it.questionId)));
        if (items.length === 0) return;
        const pageScore = items.reduce((s, it) => s + (Number(it.score) || 0), 0);
        const pageMax = items.reduce((s, it) => s + (Number(it.maxScore) || 0), 0);
        groups.push({ key: section.id || section.label, title: formatPageTitle(section.label), items, pageScore, pageMax });
    });

    // Any scored questions not matched to a current section (e.g. legacy data).
    const leftover = scores.filter((qs) => !used.has(String(qs.questionId)));
    if (leftover.length) {
        groups.push({
            key: 'other',
            title: 'Weitere Fragen',
            items: leftover,
            pageScore: leftover.reduce((s, it) => s + (Number(it.score) || 0), 0),
            pageMax: leftover.reduce((s, it) => s + (Number(it.maxScore) || 0), 0)
        });
    }

    return { groups, questionById };
};

const getQualificationScoreFromData = (qualificationEvaluation, pointEvaluation) => {
    const fromPointEvaluation = getDefaultQuestionPointTotals(pointEvaluation);
    if (fromPointEvaluation && fromPointEvaluation.maxScore > 0) {
        return fromPointEvaluation.score;
    }

    const evaluations = Array.isArray(qualificationEvaluation?.questionEvaluations)
        ? qualificationEvaluation.questionEvaluations
        : [];
    if (evaluations.length > 0) {
        return evaluations.reduce((sum, entry) => sum + (Number(entry?.score) || 0), 0);
    }

    const numericScore = Number(qualificationEvaluation?.score);
    if (Number.isFinite(numericScore)) {
        return numericScore;
    }

    return null;
};

const getQualificationMaxScoreFromData = (qualificationEvaluation, pointEvaluation, fallbackDefaultCount = 10) => {
    const fromPointEvaluation = getDefaultQuestionPointTotals(pointEvaluation);
    if (fromPointEvaluation && fromPointEvaluation.maxScore > 0) {
        return fromPointEvaluation.maxScore;
    }

    const evaluations = Array.isArray(qualificationEvaluation?.questionEvaluations)
        ? qualificationEvaluation.questionEvaluations
        : [];
    if (evaluations.length > 0) {
        const configuredMaxScore = evaluations.reduce((sum, entry) => sum + (Number(entry?.maxScore) || 0), 0);
        if (configuredMaxScore > 0) return configuredMaxScore;
    }

    return fallbackDefaultCount > 0 ? fallbackDefaultCount : 10;
};

const LEGACY_DEFAULT_SECTION_LABEL = 'Rahmendaten & Qualifikationen';

const clampDefaultQuestionsPageOrder = (order, customPageCount) => {
  const maxOrder = Math.max(1, Number(customPageCount) + 1);
  const numericOrder = Number(order);
  if (!Number.isFinite(numericOrder)) return maxOrder;
  return Math.min(Math.max(Math.round(numericOrder), 1), maxOrder);
};

// Default questions are loaded from questionnaire data

// Status icon mapping
const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
        case 'unbearbeitet':
            return (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            );
        case 'in kontakt':
            return (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
            );
        case 'interview':
            return (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
            );
        case 'probestunde':
            return (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v1m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
            );
        case 'bewerbung zurückgezogen':
            return (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h11a4 4 0 014 4v3M3 10l5-5M3 10l5 5" />
                </svg>
            );
        case 'absage':
            return (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
            );
        case 'zusage':
            return (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
            );
        default:
            return (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            );
    }
};

// Status dropdown component
function StatusDropdown({ status, onStatusChange, submissionId }) {
    const [isOpen, setIsOpen] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    // Fixed-position coords for the portalled menu (escapes the results card's
    // overflow-hidden, which otherwise clips the menu on the last/only row).
    const [menuPos, setMenuPos] = useState(null);
    const buttonRef = React.useRef(null);
    const menuRef = React.useRef(null);

    // Only settable statuses — plus the current one when it is a legacy status,
    // so the row still shows where it stands instead of hiding it.
    const statusOptions = statusChoices(status).map(({ value, label }) => ({
        value,
        label,
        icon: getStatusIcon(value),
        legacy: isLegacyStatus(value)
    }));

    const MENU_WIDTH = 224; // Tailwind w-56
    const openMenu = () => {
        const rect = buttonRef.current?.getBoundingClientRect();
        if (!rect) return;
        // ~48px per option row + borders; flip up when there isn't room below.
        const estHeight = statusOptions.length * 48 + 8 + (statusOptions.some((o) => o.legacy) ? 16 : 0);
        const spaceBelow = window.innerHeight - rect.bottom;
        const flipUp = spaceBelow < estHeight && rect.top > spaceBelow;
        let left = rect.left;
        // Keep the menu inside the viewport horizontally.
        left = Math.min(left, window.innerWidth - MENU_WIDTH - 8);
        left = Math.max(8, left);
        setMenuPos({
            left,
            width: MENU_WIDTH,
            ...(flipUp
                ? { bottom: window.innerHeight - rect.top + 4, top: 'auto' }
                : { top: rect.bottom + 4, bottom: 'auto' })
        });
        setIsOpen(true);
    };

    const handleStatusChange = async (newStatus) => {
        if (newStatus === status) {
            setIsOpen(false);
            return;
        }

        setIsUpdating(true);
        try {
            await onStatusChange(submissionId, newStatus);
            setIsOpen(false);
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error('Status konnte nicht gespeichert werden');
        } finally {
            setIsUpdating(false);
        }
    };

    const currentStatus = statusOptions.find(option => option.value === status) || statusOptions[0];

    // Close on outside click (menu lives in a portal, so check both refs) and
    // on scroll/resize (the fixed menu would otherwise detach from the button).
    React.useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (event) => {
            if (buttonRef.current?.contains(event.target)) return;
            if (menuRef.current?.contains(event.target)) return;
            setIsOpen(false);
        };
        const close = () => setIsOpen(false);
        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
        };
    }, [isOpen]);

    return (
        <div className="relative">
            <button
                ref={buttonRef}
                onClick={(e) => {
                    e.stopPropagation(); // Prevent row click
                    if (isOpen) setIsOpen(false); else openMenu();
                }}
                disabled={isUpdating}
                style={{ width: 170 }}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-colors hover:opacity-80 disabled:opacity-50 justify-center whitespace-nowrap ${getStatusColor(status)}`}
            >
                {isUpdating ? (
                    <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                ) : (
                    <div className="w-4 h-4 shrink-0">
                        {currentStatus.icon}
                    </div>
                )}
                <span className="truncate min-w-0">{isUpdating ? 'Updating...' : (status || 'Unbekannt')}</span>
                <svg className={`w-4 h-4 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {isOpen && menuPos && createPortal(
                <div
                    ref={menuRef}
                    style={{ position: 'fixed', left: menuPos.left, top: menuPos.top, bottom: menuPos.bottom, width: menuPos.width, zIndex: 1000 }}
                    className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg"
                >
                    {statusOptions.map((option) => (
                        <button
                            key={option.value}
                            onClick={(e) => {
                                e.stopPropagation(); // Prevent row click
                                handleStatusChange(option.value);
                            }}
                            disabled={option.legacy}
                            className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-medium transition-colors first:rounded-t-lg last:rounded-b-lg disabled:cursor-not-allowed enabled:hover:bg-slate-50 dark:enabled:hover:bg-slate-700 ${
                                option.value === status ? 'bg-slate-100 dark:bg-slate-700' : ''
                            }`}
                        >
                            <span className={`w-5 h-5 ${getStatusColor(option.value).split(' ')[0]} ${getStatusColor(option.value).split(' ')[1]}`}>
                                {option.icon}
                            </span>
                            <span className="min-w-0">
                                {option.label}
                                {option.legacy && (
                                    <span className="block text-xs font-normal text-slate-400 dark:text-slate-500">Alter Status, nicht mehr setzbar</span>
                                )}
                            </span>
                        </button>
                    ))}
                </div>,
                document.body
            )}
        </div>
    );
}

// Quick status switch for the detail view: one square button per status, the
// current one filled in its status color. Saves through the same handler the
// list dropdown uses.
function StatusQuickActions({ status, submissionId, onStatusChange }) {
    const [pending, setPending] = useState(null);
    const current = status || 'Unbearbeitet';

    const handleClick = async (next) => {
        if (pending || next === current) return;
        setPending(next);
        try {
            await onStatusChange?.(submissionId, next);
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error('Status konnte nicht gespeichert werden');
        } finally {
            setPending(null);
        }
    };

    return (
        <div className="flex flex-wrap items-stretch gap-2 sm:gap-3">
            {statusChoices(current).map(({ value, label }) => {
                const active = value === current;
                const legacy = isLegacyStatus(value);
                return (
                    <button
                        key={value}
                        type="button"
                        onClick={() => handleClick(value)}
                        disabled={pending !== null || legacy}
                        aria-pressed={active}
                        aria-label={legacy ? `${label} — alter Status, nicht mehr setzbar` : label}
                        className={`group relative flex-1 min-w-14 max-w-28 h-14 sm:h-16 flex items-center justify-center rounded-xl border transition-colors disabled:opacity-60 disabled:cursor-not-allowed [&>svg]:w-6 [&>svg]:h-6 sm:[&>svg]:w-7 sm:[&>svg]:h-7 ${
                            active
                                ? `${getStatusColor(value)} shadow-sm`
                                : 'bg-white dark:bg-slate-800 text-slate-400 dark:text-slate-500 border-slate-200 dark:border-slate-700 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700'
                        }`}
                    >
                        {pending === value ? (
                            <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                        ) : getStatusIcon(value)}
                        <span className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 dark:bg-slate-700">
                            {label}
                            {legacy && <span className="block text-slate-300 dark:text-slate-400">Alter Status, nicht mehr setzbar</span>}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

// Shared grid track definition for the results list. The header and every data
// row use this exact template so the columns line up perfectly. Order:
// Bewerber/Stelle · Ersteller/Zuständig · Datum · Score · Notiz · Status.
const RESULTS_GRID_COLUMNS = '2.4fr 1.3fr 0.9fr 1.4fr 2fr 1.3fr';

// Compact list-cell preview of a submission's notes feed: shows the newest post
// (author + timestamp, 3-line clamp) collapsed, expandable inline to all posts.
function NotesFeedPreview({ entries }) {
    const list = Array.isArray(entries) ? entries : [];
    const [expanded, setExpanded] = useState(false);
    // Whether the newest post's text is clamped (so a single long note can
    // still be expanded even when there are no further posts to reveal).
    const [overflowing, setOverflowing] = useState(false);
    const latestRef = useRef(null);

    useEffect(() => {
        const el = latestRef.current;
        if (el) setOverflowing(el.scrollHeight > el.clientHeight + 1);
    }, [entries, expanded]);

    if (list.length === 0) {
        return (
            <div className="w-full text-center">
                <span className="text-xs italic text-slate-300 dark:text-slate-600">Keine Notiz</span>
            </div>
        );
    }

    const latest = list[0];
    const rest = list.length - 1;

    return (
        <div className="w-full text-left mx-auto" style={{ maxWidth: 280 }} onClick={(e) => e.stopPropagation()}>
            {!expanded ? (
                <>
                    <p
                        ref={latestRef}
                        title={latest.text}
                        className="text-xs leading-relaxed text-slate-700 dark:text-slate-200 whitespace-pre-wrap wrap-break-word"
                        style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                    >
                        {latest.text}
                    </p>
                    {noteByline(latest) && (
                        <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-1 truncate">{noteByline(latest)}</div>
                    )}
                    {(rest > 0 || overflowing) && (
                        <button type="button" onClick={() => setExpanded(true)} className="mt-2 w-full text-center text-xs font-medium text-green-700 hover:text-green-800 dark:text-green-500">
                            {rest > 0 ? `+${rest} weitere · mehr` : 'mehr'}
                        </button>
                    )}
                </>
            ) : (
                <>
                    <div className="space-y-2">
                        {list.map((e) => (
                            <div key={e.id} className="rounded-lg bg-slate-50 dark:bg-slate-700 p-2">
                                <p className="text-xs leading-relaxed text-slate-700 dark:text-slate-200 whitespace-pre-wrap wrap-break-word">{e.text}</p>
                                {noteByline(e) && (
                                    <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-1 truncate">{noteByline(e)}</div>
                                )}
                            </div>
                        ))}
                    </div>
                    <button type="button" onClick={() => setExpanded(false)} className="mt-2 w-full text-center text-xs font-medium text-green-700 hover:text-green-800 dark:text-green-500">
                        weniger
                    </button>
                </>
            )}
        </div>
    );
}

// --- Ergebnisseite ---
export function ResultsPage({ navigateTo, detailId = null }) {

    const [submissions, setSubmissions] = useState([]);
    const [questionnaires, setQuestionnaires] = useState({});
    // Internal accounts for the "Zuständig" dropdown in the detail view.
    const [accounts, setAccounts] = useState([]);
    // Per-user color overrides (settings.userColors); empty = use auto palette.
    const [userColors, setUserColors] = useState({});
    // Global cutoffs for the Empfehlung badge (settings.recommendationTiers).
    const [recommendationTiers, setRecommendationTiers] = useState(null);
    const [loadingData, setLoadingData] = useState(true);
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    // Id of the submission whose full record is currently being fetched for
    // the detail view (the list only holds lightweight records).
    const [detailLoadingId, setDetailLoadingId] = useState(null);
    const [sortField, setSortField] = useState('date'); // 'date' or 'score'
    const [sortDirection, setSortDirection] = useState('desc'); // 'asc' or 'desc'
    const [currentPage, setCurrentPage] = useState(1);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    // Note feed modal: which submission, the "new post" draft, and which post
    // (if any) is being edited inline, plus a save spinner flag.
    const [noteEditSub, setNoteEditSub] = useState(null);
    const [noteDraft, setNoteDraft] = useState('');
    const [noteEditingId, setNoteEditingId] = useState(null);
    const [noteEditingText, setNoteEditingText] = useState('');
    const [noteSaving, setNoteSaving] = useState(false);

    // Filter states
    const [filters, setFilters] = useState({
        status: 'all', // 'all' or any value from SUBMISSION_STATUSES
        dateRange: 'all', // 'all', 'today', 'week', 'month', 'custom'
        customDateFrom: '',
        customDateTo: '',
        punkteRange: 'all', // 'all', '0-25', '26-50', '51-75', '76-89', '90-100' — Gesamtscore %
        templateName: 'all', // 'all' or a specific questionnaire template_name
        repeatApplicant: 'all', // 'all', 'repeat' (hat weitere Bewerbungen), 'first' (nur diese eine)
        assignedTo: 'all', // 'all', 'none', or a user id (effective Zuständig: submission override, questionnaire fallback)
        createdBy: 'all',  // 'all', 'none', or a user id (questionnaire's Ersteller)
        fragebogen2: 'all' // 'all', 'open' (noch keine Einladung verschickt)
    });
    const [showFilters, setShowFilters] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [viewedSubmissions, setViewedSubmissions] = useState(() => {
        // Load viewed submissions from localStorage on initialization
        try {
            const stored = localStorage.getItem('viewedSubmissions');
            return stored ? new Set(JSON.parse(stored)) : new Set();
        } catch (error) {
            console.error('Error loading viewed submissions from localStorage:', error);
            return new Set();
        }
    });
    const [submissionToDelete, setSubmissionToDelete] = useState(null);
    const [submissionFileUrls, setSubmissionFileUrls] = useState({});
    /** Bewerbungen mit Einladung (applicantIds) und, für Altbestand ohne Bewerbungsbezug, deren Mailadressen — für den Filter "Fragebogen 2 offen". */
    const [invited, setInvited] = useState(() => ({ applicantIds: new Set(), emailsWithoutApplicant: new Set() }));
    /** Gesetzt, wenn der Einladungsstatus nicht geladen werden konnte — der Filter warnt dann statt still zu wenig zu zeigen. */
    const [invitedKeysError, setInvitedKeysError] = useState(null);

    /** Eine gerade verschickte Einladung zählt sofort, nicht erst nach dem Neuladen. Der Dialog übergibt beim Anlegen immer eine applicantId, also ist eine frische Einladung immer bewerbungsbezogen. */
    const markInvited = useCallback((submissionId) => {
        if (!submissionId) return;
        setInvited((prev) =>
            prev.applicantIds.has(submissionId)
                ? prev
                : { ...prev, applicantIds: new Set(prev.applicantIds).add(submissionId) }
        );
    }, []);

    // Always refetch on open (no caching): the URLs carry a short-lived file
    // token for the protected `submission_files` collection and expire after
    // a few minutes.
    const loadSubmissionFiles = useCallback(async (submission) => {
        const submissionId = submission?.id;
        if (!submissionId) return;
        try {
            const fileIds = Object.values(submission.answers || {})
                .filter(a => a && Array.isArray(a.fileIds))
                .flatMap(a => a.fileIds);
            if (fileIds.length === 0) return;
            const files = await fileUploadService.getFilesByIds(fileIds);
            const urlMap = {};
            files.forEach(f => { urlMap[f.id] = f.url; });
            setSubmissionFileUrls(prev => ({ ...prev, [submissionId]: urlMap }));
        } catch (err) {
            console.warn('Failed to load submission files:', err);
        }
    }, []);

    // Fetch the full submission record (incl. heavy answer/log payloads that
    // the list view omits) and open its detail view. A brief loading state is
    // shown while the record is fetched so the detail never renders partial data.
    const openSubmissionById = useCallback(async (id) => {
        if (!id) return;
        setDetailLoadingId(id);
        try {
            const full = await dbService.getSubmission(id);
            if (full) {
                setSelectedSubmission(full);
                loadSubmissionFiles(full);
            }
        } catch (err) {
            console.error('Error opening submission detail:', err);
        } finally {
            setDetailLoadingId(null);
        }
    }, [loadSubmissionFiles]);

    // Zurueck in die Liste. Hier stand history.back(), was nur stimmte, solange
    // man genau eine Bewerbung tief war — seit man aus einer Bewerbung in eine
    // fruehere derselben Person springen kann, fuehrte ein Schritt zurueck in
    // diese andere Bewerbung, obwohl der Knopf "Zurueck zur Uebersicht" heisst.
    const goToOverview = useCallback(() => navigateTo('results'), [navigateTo]);

    // Function to update submission status
    const updateSubmissionStatus = async (submissionId, newStatus) => {
        try {
            requireAuth();
            // Update in PocketBase
            await pb.collection('submissions').update(submissionId, {
                status: newStatus
            });
            
            // Update local state
            setSubmissions(prev => prev.map(sub => 
                sub.id === submissionId 
                    ? { ...sub, status: newStatus }
                    : sub
            ));
            
            console.log(`Status updated to ${newStatus} for submission ${submissionId}`);
        } catch (error) {
            console.error('Error updating status:', error);
            throw error; // Re-throw to handle in the dropdown component
        }
    };

    const currentAuthor = () => {
        const u = pb.authStore.record || pb.authStore.model;
        return u ? { id: u.id, name: u.name || u.username || u.email || '' } : { id: '', name: '' };
    };

    const newNoteId = () =>
        (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `n_${Date.now()}_${Math.random().toString(36).slice(2)}`;

    // Persist a new entries array for a submission and reflect it in list state.
    const persistNoteEntries = async (submissionId, entries) => {
        await dbService.updateSubmission(submissionId, { noteEntries: entries });
        setSubmissions((prev) => prev.map((sub) =>
            sub.id === submissionId
                ? { ...sub, noteEntries: entries, notes: entries[0]?.text || '', analysis: { ...(sub.analysis || {}), noteEntries: entries, notes: entries[0]?.text || '' } }
                : sub
        ));
        setNoteEditSub((cur) => (cur && cur.id === submissionId ? { ...cur, noteEntries: entries } : cur));
    };

    const openNoteEditor = (sub) => {
        setNoteEditSub({ ...sub, noteEntries: Array.isArray(sub.noteEntries) ? sub.noteEntries : migrateNotes(sub.analysis) });
        setNoteDraft('');
        setNoteEditingId(null);
        setNoteEditingText('');
    };

    const closeNoteEditor = () => {
        setNoteEditSub(null);
        setNoteDraft('');
        setNoteEditingId(null);
        setNoteEditingText('');
    };

    const handleAddNote = async () => {
        if (!noteEditSub || !noteDraft.trim()) return;
        setNoteSaving(true);
        try {
            const entries = addNoteEntry(noteEditSub.noteEntries || [], {
                id: newNoteId(), text: noteDraft, author: currentAuthor(), createdAt: new Date().toISOString(),
            });
            await persistNoteEntries(noteEditSub.id, entries);
            setNoteDraft('');
            toast.success('Notiz hinzugefügt');
        } catch (error) {
            console.error('Error adding note:', error);
            toast.error('Notiz konnte nicht gespeichert werden');
        } finally {
            setNoteSaving(false);
        }
    };

    const handleSaveNoteEdit = async () => {
        if (!noteEditSub || !noteEditingId || !noteEditingText.trim()) return;
        setNoteSaving(true);
        try {
            const entries = editNoteEntry(noteEditSub.noteEntries || [], noteEditingId, noteEditingText, new Date().toISOString());
            await persistNoteEntries(noteEditSub.id, entries);
            setNoteEditingId(null);
            setNoteEditingText('');
            toast.success('Notiz gespeichert');
        } catch (error) {
            console.error('Error editing note:', error);
            toast.error('Notiz konnte nicht gespeichert werden');
        } finally {
            setNoteSaving(false);
        }
    };

    const handleDeleteNote = async (entryId) => {
        if (!noteEditSub) return;
        setNoteSaving(true);
        try {
            const entries = deleteNoteEntry(noteEditSub.noteEntries || [], entryId);
            await persistNoteEntries(noteEditSub.id, entries);
            toast.success('Notiz gelöscht');
        } catch (error) {
            console.error('Error deleting note:', error);
            toast.error('Notiz konnte nicht gelöscht werden');
        } finally {
            setNoteSaving(false);
        }
    };

    // Helper function to sort submissions
    const sortSubmissions = useCallback((submissionsArray, field = sortField, direction = sortDirection) => {
        const sorted = submissionsArray.slice();
        
        if (field === 'date') {
            sorted.sort((a, b) => {
                const dateA = new Date(a.submittedAt);
                const dateB = new Date(b.submittedAt);
                return direction === 'asc' ? dateA - dateB : dateB - dateA;
            });
        } else if (field === 'score') {
            // Compare the total point score shown in the "Punkte" badge
            // (point_evaluation.totalScore), falling back to the legacy
            // analysis.score for submissions without a point evaluation.
            const totalScore = (s) => {
                const total = Number(s.point_evaluation?.totalScore);
                return Number.isFinite(total) ? total : (Number(s.analysis?.score) || 0);
            };
            sorted.sort((a, b) => {
                const scoreA = totalScore(a);
                const scoreB = totalScore(b);
                return direction === 'asc' ? scoreA - scoreB : scoreB - scoreA;
            });
        } else if (field === 'status') {
            sorted.sort((a, b) => {
                const statusA = a.status || 'Unbearbeitet';
                const statusB = b.status || 'Unbearbeitet';
                
                const orderA = STATUS_CONFIG[statusA]?.order || 999;
                const orderB = STATUS_CONFIG[statusB]?.order || 999;
                
                if (orderA === orderB) {
                    // If same status, sort by name
                    return direction === 'asc' ? 
                        statusA.localeCompare(statusB) : 
                        statusB.localeCompare(statusA);
                }
                
                return direction === 'asc' ? orderA - orderB : orderB - orderA;
            });
        }
        
        return sorted;
    }, [sortField, sortDirection]);

    // Effective Zuständig of a submission: its own assignee wins; otherwise
    // the questionnaire's default (set in the questionnaire editor) applies.
    const effectiveAssignee = useCallback((sub) => {
        if (sub.assignedTo?.id) return sub.assignedTo;
        return questionnaires[sub.questionnaireId]?.assignedTo || null;
    }, [questionnaires]);

    // Applied-for job title (Stelle) per submission. Maps submission id →
    // display string; submissions without one are absent.
    //
    // Der Text wird beim Absenden abgeleitet und in submissions.job_title
    // gespeichert. Vorher geschah das hier: aus den Fragen aller Fragebogen und
    // den Antworten aller Bewerbungen — beides musste dafuer vollstaendig
    // geladen werden und machte den Loewenanteil der 36 MB dieser Seite aus.
    const jobTitlesBySubmission = useMemo(() => {
        const map = {};
        submissions.forEach((sub) => {
            const title = String(sub.jobTitle || '').trim();
            if (title) map[sub.id] = title;
        });
        return map;
    }, [submissions]);

    // Wiederbewerber: alle Bewerbungen je Namensschlüssel. Bewusst über den
    // Gesamtbestand und nicht über die gefilterte Liste — sonst verschwände ein
    // Treffer, sobald nach Status oder Fragebogen gefiltert wird.
    const nameIndex = useMemo(() => buildNameIndex(submissions), [submissions]);
    const relatedBySubmission = useMemo(() => {
        const map = {};
        nameIndex.forEach((group) => {
            if (group.length < 2) return;
            group.forEach((sub) => { map[sub.id] = findRelatedApplications(sub, nameIndex); });
        });
        return map;
    }, [nameIndex]);

    // Für die Anzeige reichen die Felder, die die Liste ohnehin hält —
    // nachgeladen wird nur, was die Trefferstufe braucht.
    const describeRelated = useCallback((related = []) => related.map((entry) => ({
        id: entry.id,
        submittedAt: entry.submittedAt,
        status: entry.status,
        jobTitle: entry.jobTitle || '',
        questionnaireTitle: questionnaires[entry.questionnaireId]?.title || 'Unbekannter Fragebogen'
    })), [questionnaires]);

    const selectedRelated = useMemo(
        () => (selectedSubmission ? describeRelated(relatedBySubmission[selectedSubmission.id]) : []),
        [selectedSubmission, relatedBySubmission, describeRelated]
    );

    // Helper function to filter submissions
    const filterSubmissions = useCallback((submissionsArray) => {
        const query = searchQuery.trim().toLowerCase();
        // Every whitespace-separated token must appear somewhere in the combined
        // haystack (applicant name + questionnaire title + Stelle), so a query
        // like "IT Minijob" matches across fields instead of only within one.
        const queryTokens = query ? query.split(/\s+/).filter(Boolean) : [];
        return submissionsArray.filter(submission => {
            // Search matches the applicant name, the questionnaire name and the
            // applied-for job title (Stelle).
            if (queryTokens.length > 0) {
                const haystack = [
                    submission.applicantName,
                    questionnaires[submission.questionnaireId]?.title,
                    jobTitlesBySubmission[submission.id]
                ].filter(Boolean).join(' ').toLowerCase();
                if (!queryTokens.every(token => haystack.includes(token))) {
                    return false;
                }
            }

            // Status filter
            if (filters.status !== 'all' && submission.status !== filters.status) {
                return false;
            }

            // Zuständig (effective: submission override, questionnaire fallback)
            // and Ersteller filter (resolved via the submission's questionnaire)
            if (filters.assignedTo !== 'all' || filters.createdBy !== 'all') {
                const ques = questionnaires[submission.questionnaireId];
                if (filters.assignedTo !== 'all') {
                    const assignedId = effectiveAssignee(submission)?.id || 'none';
                    if (filters.assignedTo !== assignedId) return false;
                }
                if (filters.createdBy !== 'all') {
                    const creatorId = ques?.createdBy?.id || 'none';
                    if (filters.createdBy !== creatorId) return false;
                }
            }

            // Date filter
            if (filters.dateRange !== 'all') {
                const submissionDate = new Date(submission.submittedAt);
                const now = new Date();
                const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                
                switch (filters.dateRange) {
                    case 'today':
                        const submissionToday = new Date(submissionDate.getFullYear(), submissionDate.getMonth(), submissionDate.getDate());
                        if (submissionToday.getTime() !== today.getTime()) return false;
                        break;
                    case 'week':
                        const weekAgo = new Date(today);
                        weekAgo.setDate(weekAgo.getDate() - 7);
                        if (submissionDate < weekAgo) return false;
                        break;
                    case 'month':
                        const monthAgo = new Date(today);
                        monthAgo.setMonth(monthAgo.getMonth() - 1);
                        if (submissionDate < monthAgo) return false;
                        break;
                    case 'custom':
                        if (filters.customDateFrom) {
                            const fromDate = new Date(filters.customDateFrom);
                            if (submissionDate < fromDate) return false;
                        }
                        if (filters.customDateTo) {
                            const toDate = new Date(filters.customDateTo);
                            toDate.setHours(23, 59, 59, 999); // End of day
                            if (submissionDate > toDate) return false;
                        }
                        break;
                    default:
                        break;
                }
            }

            // Punkte (Gesamtscore %) filter
            if (filters.punkteRange !== 'all') {
                const pe = submission.point_evaluation;
                const max = Number(pe?.maxScore) || 0;
                const pct = max > 0 ? Math.round((Number(pe?.totalScore) || 0) / max * 100) : null;
                if (pct === null) return false; // unscored submissions never match a Punkte range
                const [minScore, maxScore] = filters.punkteRange.split('-').map(Number);
                if (pct < minScore || pct > maxScore) return false;
            }

            // Vorlage (template) filter
            if (filters.templateName !== 'all') {
                const tn = questionnaires[submission.questionnaireId]?.templateName || null;
                if (tn !== filters.templateName) return false;
            }

            // Mehrfachbewerbung: greift auf denselben Namensindex zu wie das
            // Abzeichen in der Zeile — "Nur Wiederbewerber" zeigt also genau
            // die Zeilen, die eins tragen.
            if (filters.repeatApplicant !== 'all') {
                const isRepeat = Boolean(relatedBySubmission[submission.id]);
                if (filters.repeatApplicant === 'repeat' && !isRepeat) return false;
                if (filters.repeatApplicant === 'first' && isRepeat) return false;
            }

            // Wird die Mailadresse im Dialog vor dem Senden geändert, landet die
            // Einladung unter der geänderten Adresse — die aus den Antworten
            // abgeleitete taucht dann nie in invited.emailsWithoutApplicant auf,
            // und die Zeile bleibt unter "Fragebogen 2 offen" sichtbar, obwohl
            // längst gesendet wurde. Die Karte in der Bewerbung selbst stimmt
            // trotzdem, weil listForApplicant zusätzlich über die Bewerbung
            // (applicant) sucht. Das ist die richtige Richtung für einen
            // Filter: eher zu viel zeigen als jemanden übersehen. Der
            // Adress-Fallback gilt seitdem nur noch für Einladungen ohne
            // Bewerbungsbezug — eine Einladung, die an eine andere Bewerbung
            // gebunden ist, darf eine neue Bewerbung derselben Adresse nicht
            // als "schon eingeladen" markieren.
            if (filters.fragebogen2 === 'open') {
                if (invited.applicantIds.has(submission.id)) return false;
                const email = contactKeysFromAnswers(submission.answers).emails[0] || '';
                if (email && invited.emailsWithoutApplicant.has(email)) return false;
            }

            return true;
        });
    }, [filters, searchQuery, questionnaires, effectiveAssignee, jobTitlesBySubmission, relatedBySubmission, invited]);

    // Single source of truth for the visible list: filtered + sorted once per
    // change (replaces three inline recomputations that ran on every render).
    const processedSubmissions = useMemo(
        () => sortSubmissions(filterSubmissions(submissions)),
        [submissions, filterSubmissions, sortSubmissions]
    );

    // Client-side pagination: load everything, render one page at a time so the
    // DOM stays small and filters/sort remain instant across the whole dataset.
    const pageInfo = paginate(processedSubmissions.length, currentPage, RESULTS_PAGE_SIZE);
    const pageItems = processedSubmissions.slice(pageInfo.startIndex, pageInfo.endIndex);

    // Jump back to page 1 when the filtered/sorted set changes shape.
    useEffect(() => {
        setCurrentPage(1);
    }, [filters, searchQuery, sortField, sortDirection]);

    // Clamp the page when the result count shrinks (e.g. after a deletion).
    useEffect(() => {
        if (currentPage > pageInfo.pageCount) setCurrentPage(pageInfo.pageCount);
    }, [pageInfo.pageCount, currentPage]);

    // Load submissions and questionnaires with PocketBase subscription
    const loadData = useCallback(async () => {
        setLoadingData(true);
        try {
            requireAuth();

            const [subs, ques, users, settings] = await Promise.all([
                dbService.getSubmissionsForList(),
                dbService.getQuestionnairesForList(),
                dbService.getUsers(),
                dbService.getSettings()
            ]);

            // Convert questionnaires array to object for easy lookup
            const quesObj = ques.reduce((acc, q) => {
                acc[q.id] = q;
                return acc;
            }, {});

            setQuestionnaires(quesObj);
            setAccounts(users);
            setUserColors(settings?.userColors || {});
            setRecommendationTiers(settings?.recommendationTiers || null);
            setSubmissions(sortSubmissions(subs, sortField, sortDirection));
        } catch (error) {
            console.error('Error loading data:', error);
            setSubmissions([]);
        } finally {
            setLoadingData(false);
        }
    }, [sortSubmissions, sortField, sortDirection]);

    // Reassign the internal account responsible ("Zuständig") for a single
    // submission. The questionnaire's default Zuständig is edited in the
    // questionnaire editor, not here. Returns the result so the detail view
    // can sync its local copy.
    const handleAssignSubmission = useCallback(async (submissionId, userId) => {
        if (!submissionId) return { success: false };
        const result = await dbService.updateSubmissionAssignee(submissionId, userId);
        if (!result?.success) {
            toast.error('Zuweisung fehlgeschlagen');
            return result || { success: false };
        }
        setSubmissions((prev) => prev.map((sub) =>
            sub.id === submissionId ? { ...sub, assignedTo: result.assignedTo } : sub
        ));
        toast.success('Zuständigkeit aktualisiert');
        return result;
    }, []);

    // Set up PocketBase subscriptions and load initial data
    useEffect(() => {
        // Load initial data
        loadData();

        // Eine Abfrage für die ganze Liste. Je Bewerbung zu fragen wären bei
        // 400 Bewerbungen 400 Abfragen.
        inviteService.listInvitedKeys().then((result) => {
            if (result.success) setInvited(result.data);
            setInvitedKeysError(result.success ? null : result.error || 'Einladungsstatus konnte nicht geladen werden');
        });

        // Temporarily disable realtime subscriptions due to CORS issues
        // TODO: Re-enable once CORS is configured on PocketBase server
        /*
        // Subscribe to all submissions changes
        pb.collection('submissions').subscribe('*', (e) => {
            console.log('Submissions subscription event:', e.action, e.record);
            
            // Reload data when any change occurs
            loadData();
        });

        // Cleanup subscription on unmount
        return () => {
            pb.collection('submissions').unsubscribe('*');
        };
        */
    }, [loadData]);

    // Track when a submission is clicked/viewed
    const handleSubmissionClick = (submission) => {
        // Mark as viewed
        const newViewedSubmissions = new Set([...viewedSubmissions, submission.id]);
        setViewedSubmissions(newViewedSubmissions);
        
        // Save to localStorage
        try {
            localStorage.setItem('viewedSubmissions', JSON.stringify([...newViewedSubmissions]));
        } catch (error) {
            console.error('Error saving viewed submissions to localStorage:', error);
        }
        
        // Nur navigieren — die Detailansicht haengt am ?id= der URL, und der
        // Effect weiter unten laedt den vollen Datensatz nach. Frueher schob
        // diese Funktion die URL selbst; dadurch wusste AdminApp nichts von
        // der offenen Bewerbung und ein Klick auf "Ergebnisse" in der Navi
        // aenderte zwar die URL, schloss die Detailansicht aber nicht.
        navigateTo('results', submission.id);
    };

    // Print function for exporting results


    // Delete submission function (list view)
    const deleteSubmission = async (submissionId, event) => {
        event.stopPropagation(); // Prevent clicking the list item
        setSubmissionToDelete(submissionId);
        setShowDeleteModal(true);
    };

    // Confirm delete function
    const confirmDelete = async () => {
        if (!submissionToDelete) return;
        
        try {
            if (DB_CONFIG.type === 'pocketbase') {
                await dbService.deleteSubmission(submissionToDelete);
                
                // Manually remove the deleted submission from local state
                setSubmissions(prev => prev.filter(sub => sub.id !== submissionToDelete));
                
                // Close detail view if the deleted submission was selected
                if (selectedSubmission && selectedSubmission.id === submissionToDelete) {
                    setSelectedSubmission(null);
                }
            } else {
                // No local fallback
            }
            
            setShowDeleteModal(false);
            setSubmissionToDelete(null);
        } catch (error) {
            console.error('Error deleting submission:', error);
            alert('Fehler beim Löschen der Bewerbung');
        }
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('desc');
        }
    };



    // Re-sort when sort criteria change
    useEffect(() => {
        setSubmissions(prev => sortSubmissions(prev));
    }, [sortField, sortDirection, sortSubmissions]);

    // Die geoeffnete Bewerbung haengt allein am ?id= der URL, das AdminApp als
    // `submissionId` hereinreicht. Ein Weg fuer alle Faelle: Klick auf eine
    // Zeile, Sprung in eine fruehere Bewerbung derselben Person, "Ergebnisse"
    // in der Navigation, Browser-Zurueck und der Direktaufruf einer Detail-URL.
    // Zuvor fuehrten AdminApp und diese Seite getrennt Buch, was bei jedem Weg
    // ausser Zeilenklick und Browser-Zurueck auseinanderlief.
    const openedIdRef = useRef(null);
    useEffect(() => {
        if (!detailId) {
            openedIdRef.current = null;
            setDetailLoadingId(null);
            setSelectedSubmission(null);
            return;
        }
        // Ein Wechsel des Datensatzes (z.B. nach dem Speichern einer Notiz)
        // traegt dieselbe Id und darf kein zweites Laden ausloesen.
        if (openedIdRef.current === detailId) return;
        openedIdRef.current = detailId;
        openSubmissionById(detailId);
    }, [detailId, openSubmissionById]);

    // While the full record for a freshly opened applicant is being fetched,
    // show a brief spinner instead of the list (avoids partial detail render).
    if (detailLoadingId && !selectedSubmission) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
                    <p className="mt-4 text-slate-600 dark:text-slate-400">Bewerbung wird geladen...</p>
                </div>
            </div>
        );
    }

    if (selectedSubmission) return <SubmissionDetail submission={selectedSubmission} relatedApplications={selectedRelated} onOpenSubmission={(id) => handleSubmissionClick({ id })} submissionFileUrls={submissionFileUrls} accounts={accounts} recommendationTiers={recommendationTiers} onAssignSubmission={handleAssignSubmission} onStatusChange={updateSubmissionStatus} onInvitationSent={markInvited} onSetUpJob={() => navigateTo('stellenanzeigen')} onBack={goToOverview} onUpdate={(updatedSubmission) => {
        setSubmissions(prev => prev.map(sub =>
            sub.id === updatedSubmission.id ? updatedSubmission : sub
        ));
        setSelectedSubmission(updatedSubmission);
    }} onDelete={(deletedSubmissionId) => {
        // Die geloeschte Bewerbung aus der Liste nehmen und zurueck in die
        // Uebersicht — nicht einen Schritt zurueck, der sonst in der zuvor
        // geoeffneten Bewerbung derselben Person landen wuerde.
        setSubmissions(prev => prev.filter(sub => sub.id !== deletedSubmissionId));
        goToOverview();
    }} />;
    return (
        <div className="min-h-screen pt-8 pb-24 relative overflow-hidden">
            {/* Dynamic Flowing Lines */}
            <FlowingLines theme="blue" opacity="60" />
            
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <header className="mb-8 text-center">
                    <ModernButton 
                        onClick={() => navigateTo('dashboard')} 
                        variant="ghost" 
                        size="sm" 
                        className="mb-6"
                        icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>}
                    >
                        Zurück zum Dashboard
                    </ModernButton>
                    <h1 className="text-3xl md:text-5xl font-bold rainbow-headline mb-6 leading-tight pb-2">
                        Ergebnisse & Auswertung
                    </h1>
                    <p className="text-xl text-slate-600 dark:text-slate-300 max-w-2xl mx-auto mb-6 mt-2">
                        Alle eingegangenen Bewerbungen mit KI-gestützter Analyse
                    </p>
                </header>
                
                {/* Filter Section */}
                <div className="mb-6">
                    <div className="bg-white/90 dark:bg-slate-800/80 backdrop-blur-xs rounded-xl shadow-lg border border-slate-200/50 dark:border-slate-700/50 overflow-hidden">
                        {/* Name search */}
                        <div className="p-4 border-b border-slate-200 dark:border-slate-700">
                            <div className="relative">
                                <svg
                                    className="absolute w-5 h-5 text-slate-400 dark:text-slate-500 pointer-events-none"
                                    style={{ left: '0.75rem', top: '50%', transform: 'translateY(-50%)' }}
                                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                                </svg>
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Nach Name, Fragebogen oder Stelle suchen"
                                    style={{ paddingLeft: '2.5rem', paddingRight: '2.5rem' }}
                                    className="w-full py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 focus:border-green-500"
                                    aria-label="Suchen"
                                />
                                {searchQuery && (
                                    <button
                                        type="button"
                                        onClick={() => setSearchQuery('')}
                                        className="absolute p-1 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 rounded-full hover:bg-slate-100 dark:hover:bg-slate-600"
                                        style={{ right: '0.5rem', top: '50%', transform: 'translateY(-50%)' }}
                                        aria-label="Suche zurücksetzen"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="p-4 border-b border-slate-200 dark:border-slate-700">
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className="flex items-center gap-2 text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100 transition-colors"
                            >
                                <svg className={`w-5 h-5 transition-transform ${showFilters ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                                <span className="font-medium">Filter</span>
                                {(() => {
                                    const hasFilters = filters.status !== 'all' || filters.dateRange !== 'all' || filters.punkteRange !== 'all' || filters.templateName !== 'all' || filters.repeatApplicant !== 'all' || filters.assignedTo !== 'all' || filters.createdBy !== 'all' || filters.fragebogen2 !== 'all';
                                    return hasFilters && (
                                        <span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs px-2 py-1 rounded-full">
                                            Aktiv
                                        </span>
                                    );
                                })()}
                            </button>
                        </div>
                        
                        {showFilters && (
                            <div className="p-4 space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                    {/* Date Filter */}
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                            Eingegangen
                                        </label>
                                        <select
                                            value={filters.dateRange}
                                            onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
                                            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="all">Alle Daten</option>
                                            <option value="today">Heute</option>
                                            <option value="week">Letzte 7 Tage</option>
                                            <option value="month">Letzter Monat</option>
                                            <option value="custom">Benutzerdefiniert</option>
                                        </select>
                                    </div>

                                    {/* Vorlage (template) Filter */}
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                            Vorlage
                                        </label>
                                        <select
                                            value={filters.templateName}
                                            onChange={(e) => setFilters(prev => ({ ...prev, templateName: e.target.value }))}
                                            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="all">Alle Vorlagen</option>
                                            {[...new Set(Object.values(questionnaires).map(q => q?.templateName).filter(Boolean))].sort().map(tn => (
                                                <option key={tn} value={tn}>{tn}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Mehrfachbewerbung Filter */}
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                            Mehrfachbewerbung
                                        </label>
                                        <select
                                            value={filters.repeatApplicant}
                                            onChange={(e) => setFilters(prev => ({ ...prev, repeatApplicant: e.target.value }))}
                                            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="all">Alle Bewerbungen</option>
                                            <option value="repeat">Nur Wiederbewerber</option>
                                            <option value="first">Nur Erstbewerbungen</option>
                                        </select>
                                    </div>

                                    {/* Punkte (Gesamtscore %) Filter */}
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                            Punkte
                                        </label>
                                        <select
                                            value={filters.punkteRange}
                                            onChange={(e) => setFilters(prev => ({ ...prev, punkteRange: e.target.value }))}
                                            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="all">Alle Punkte</option>
                                            <option value="0-25">0-25 %</option>
                                            <option value="26-50">26-50 %</option>
                                            <option value="51-75">51-75 %</option>
                                            <option value="76-89">76-89 %</option>
                                            <option value="90-100">90-100 %</option>
                                        </select>
                                    </div>

                                    {/* Status Filter */}
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                            Status
                                        </label>
                                        <select
                                            value={filters.status}
                                            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                                            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="all">Alle Status</option>
                                            {(() => {
                                                // Get unique status values from existing submissions
                                                const uniqueStatuses = [...new Set(submissions.map(sub => sub.status).filter(Boolean))];
                                                
                                                // Filter to only show statuses that exist in the data
                                                return Object.entries(STATUS_CONFIG)
                                                    .filter(([value]) => uniqueStatuses.includes(value))
                                                    .map(([value, config]) => (
                                                        <option key={value} value={value}>
                                                            {config.label}
                                                        </option>
                                                    ));
                                            })()}
                                        </select>
                                    </div>

                                    {/* Zuständig Filter */}
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                            Zuständig
                                        </label>
                                        <select
                                            value={filters.assignedTo}
                                            onChange={(e) => setFilters(prev => ({ ...prev, assignedTo: e.target.value }))}
                                            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="all">Alle Zuständigen</option>
                                            <option value="none">—</option>
                                            {accounts.map((account) => (
                                                <option key={account.id} value={account.id}>{userDisplayName(account)}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Ersteller Filter */}
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                            Ersteller
                                        </label>
                                        <select
                                            value={filters.createdBy}
                                            onChange={(e) => setFilters(prev => ({ ...prev, createdBy: e.target.value }))}
                                            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="all">Alle Ersteller</option>
                                            <option value="none">—</option>
                                            {accounts.map((account) => (
                                                <option key={account.id} value={account.id}>{userDisplayName(account)}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Fragebogen 2 Filter */}
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                            Fragebogen 2
                                        </label>
                                        <select
                                            value={filters.fragebogen2}
                                            onChange={(e) => setFilters(prev => ({ ...prev, fragebogen2: e.target.value }))}
                                            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="all">Fragebogen 2: alle</option>
                                            <option value="open">Fragebogen 2 offen</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Custom Date Range */}
                                {filters.dateRange === 'custom' && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                                Von
                                            </label>
                                            <input
                                                type="date"
                                                value={filters.customDateFrom}
                                                onChange={(e) => setFilters(prev => ({ ...prev, customDateFrom: e.target.value }))}
                                                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                                Bis
                                            </label>
                                            <input
                                                type="date"
                                                value={filters.customDateTo}
                                                onChange={(e) => setFilters(prev => ({ ...prev, customDateTo: e.target.value }))}
                                                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Clear Filters Button */}
                                <div className="flex justify-end">
                                    <button
                                        onClick={() => setFilters({
                                            status: 'all',
                                            dateRange: 'all',
                                            customDateFrom: '',
                                            customDateTo: '',
                                            punkteRange: 'all',
                                            templateName: 'all',
                                            repeatApplicant: 'all',
                                            assignedTo: 'all',
                                            createdBy: 'all',
                                            fragebogen2: 'all'
                                        })}
                                        className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
                                    >
                                        Alle Filter zurücksetzen
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {filters.fragebogen2 === 'open' && invitedKeysError && (
                    <p className="mb-4 text-sm text-amber-700 dark:text-amber-400">
                        Einladungsstatus konnte nicht geladen werden — die Liste zeigt daher möglicherweise noch Bewerbungen, die bereits eingeladen wurden.
                    </p>
                )}

                <div className="bg-white/90 dark:bg-slate-800/80 backdrop-blur-xs rounded-2xl shadow-xl dark:shadow-lg border border-slate-200/50 dark:border-slate-700/50 overflow-hidden">
                    {/* Result count header - centered */}
                    <div className="text-center p-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50">
                        <div className="text-sm font-medium text-slate-600 dark:text-slate-400">
                            {(() => {
                              const filteredSubmissions = processedSubmissions;
                              const hasFilters = filters.status !== 'all' || filters.dateRange !== 'all' || filters.punkteRange !== 'all' || filters.templateName !== 'all' || filters.repeatApplicant !== 'all' || filters.assignedTo !== 'all' || filters.createdBy !== 'all' || filters.fragebogen2 !== 'all';
                              return (
                                <>
                                  {filteredSubmissions.length} {filteredSubmissions.length === 1 ? 'Ergebnis' : 'Ergebnisse'}
                                  {hasFilters && filteredSubmissions.length !== submissions.length && (
                                    <span className="ml-2 text-slate-400 dark:text-slate-500">
                                      (von {submissions.length} gesamt)
                                    </span>
                                  )}
                                </>
                              );
                            })()}
                        </div>
                    </div>
                    
                    {/* Results grid layout — defined inline because the prebuilt
                        output.css lacks the sm:grid / sm:block responsive display
                        utilities, so Tailwind classes alone can't drive it. */}
                    <style>{`
.results-header { display: none; }
.results-grid > * { min-width: 0; }
/* Below lg: purpose-built mobile/tablet card (.results-card). At lg+: the wide
   desktop table (.results-grid). Toggled here because the prebuilt output.css
   lacks the responsive display utilities to drive it via Tailwind. */
.results-grid { display: none; }
.results-card { display: block; }
@media (min-width: 1024px) {
  .results-header { display: block; }
  .results-grid {
    display: grid;
    grid-template-columns: ${RESULTS_GRID_COLUMNS};
    column-gap: 16px;
    align-items: center;
  }
  .results-card { display: none; }
}
`}</style>
                    {/* Sorting header - grid aligned with data rows (desktop only) */}
                    <div className="results-header px-4 sm:px-6 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                        <div className="results-grid ml-8">
                            {/* Bewerber / Stelle */}
                            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">Bewerber / Stelle</span>

                            {/* Ersteller / Zuständig */}
                            <span className="text-center text-xs font-semibold text-slate-500 dark:text-slate-400">Ersteller / Zuständig</span>

                            {/* Eingangsdatum sorting */}
                            <div className="flex justify-center">
                                <button
                                    onClick={() => handleSort('date')}
                                    className={`flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded transition ${sortField === 'date' ? 'text-green-700 bg-green-50 dark:bg-green-900/20' : 'text-slate-500 dark:text-slate-400 hover:text-green-700 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                                    title="Datum und Uhrzeit des Bewerbungseingangs"
                                >
                                    Eingegangen
                                    <svg className={`w-3 h-3 transition-transform ${sortField === 'date' ? 'text-green-700' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        {sortField === 'date' && sortDirection === 'asc' ? (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                        ) : (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        )}
                                    </svg>
                                </button>
                            </div>

                            {/* Punkte sorting */}
                            <div className="flex justify-center">
                                <button
                                    onClick={() => handleSort('score')}
                                    className={`flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded transition ${sortField === 'score' ? 'text-green-700 bg-green-50 dark:bg-green-900/20' : 'text-slate-500 dark:text-slate-400 hover:text-green-700 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                                >
                                    Punkte
                                    <svg className={`w-3 h-3 transition-transform ${sortField === 'score' ? 'text-green-700' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        {sortField === 'score' && sortDirection === 'asc' ? (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                        ) : (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        )}
                                    </svg>
                                </button>
                            </div>

                            {/* Notiz */}
                            <span className="text-center text-xs font-semibold text-slate-500 dark:text-slate-400">Notiz</span>

                            {/* Status sorting */}
                            <div className="flex justify-center">
                                <button
                                    onClick={() => handleSort('status')}
                                    className={`flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded transition ${sortField === 'status' ? 'text-green-700 bg-green-50 dark:bg-green-900/20' : 'text-slate-500 dark:text-slate-400 hover:text-green-700 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                                >
                                    Status
                                    <svg className={`w-3 h-3 transition-transform ${sortField === 'status' ? 'text-green-700' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        {sortField === 'status' && sortDirection === 'asc' ? (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                        ) : (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        )}
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                  {pageInfo.pageCount > 1 && (
                    <div className="px-3 sm:px-6 py-2 border-b border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-700/30">
                      <Pagination
                        currentPage={pageInfo.page}
                        pageCount={pageInfo.pageCount}
                        from={pageInfo.from}
                        to={pageInfo.to}
                        total={pageInfo.total}
                        onPageChange={setCurrentPage}
                      />
                    </div>
                  )}
                  <ul className="divide-y divide-slate-200 dark:divide-slate-700">
                    {(() => {
                      return processedSubmissions.length > 0 ? pageItems.map(sub => (
                            <li key={sub.id} className="group relative p-4 sm:p-6 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer">
                                {/* Assignee color accent (rounded pill on the left edge) */}
                                {getUserColor(effectiveAssignee(sub), userColors) && (
                                    <span
                                        aria-hidden="true"
                                        className="absolute"
                                        style={{ left: 5, top: 12, bottom: 12, width: 4, borderRadius: 9999, backgroundColor: getUserColor(effectiveAssignee(sub), userColors) }}
                                    />
                                )}
                                {/* Delete button - appears on hover */}
                                <button 
                                    onClick={(e) => deleteSubmission(sub.id, e)}
                                    className="absolute left-2 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-2 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-full z-10"
                                    title="Ergebnis löschen"
                                >
                                    <TrashIcon />
                                </button>

                                {/* Mobile / tablet card — purpose-built (below lg); the desktop table below is hidden there */}
                                <div onClick={() => handleSubmissionClick(sub)} className="results-card" style={{ marginLeft: '0.75rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem' }}>
                                        <div style={{ minWidth: 0 }}>
                                            <p className="font-semibold text-green-700 dark:text-green-500 text-base truncate" style={{ lineHeight: 1.2 }}>{sub.applicantName}</p>
                                            {relatedBySubmission[sub.id] && (
                                                <div style={{ marginTop: '4px' }}>
                                                    <RepeatApplicantBadge
                                                        submission={sub}
                                                        related={relatedBySubmission[sub.id]}
                                                        onOpen={(id) => handleSubmissionClick({ id })}
                                                    />
                                                </div>
                                            )}
                                            <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{questionnaires[sub.questionnaireId]?.title || 'Unbekannter Fragebogen'}</p>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                                            {sub.point_evaluation && sub.point_evaluation.totalScore !== undefined && (
                                                <span className={`text-sm font-bold rounded-full ${getPointScoreColor(sub.point_evaluation.totalScore, sub.point_evaluation.maxScore)} ${getPointScoreTextColor(sub.point_evaluation.totalScore, sub.point_evaluation.maxScore)}`} style={{ padding: '4px 10px', whiteSpace: 'nowrap' }}>
                                                    {sub.point_evaluation.totalScore}/{sub.point_evaluation.maxScore}
                                                </span>
                                            )}
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); deleteSubmission(sub.id, e); }}
                                                className="rounded-full text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                                                style={{ padding: '6px', flexShrink: 0 }}
                                                aria-label="Ergebnis löschen"
                                                title="Ergebnis löschen"
                                            >
                                                <TrashIcon />
                                            </button>
                                        </div>
                                    </div>

                                    {(questionnaires[sub.questionnaireId]?.templateName || jobTitlesBySubmission[sub.id]) && (
                                        <div className="text-xs text-slate-500 dark:text-slate-400" style={{ marginTop: '4px' }}>
                                            {questionnaires[sub.questionnaireId]?.templateName && (
                                                <div className="truncate">Vorlage: <span className="text-slate-600 dark:text-slate-300">{questionnaires[sub.questionnaireId].templateName}</span></div>
                                            )}
                                            {jobTitlesBySubmission[sub.id] && (
                                                <div className="truncate">Stelle: <span className="text-slate-700 dark:text-slate-200 font-medium">{jobTitlesBySubmission[sub.id]}</span></div>
                                            )}
                                        </div>
                                    )}

                                    {Array.isArray(sub.point_evaluation?.pageScores) && sub.point_evaluation.pageScores.length > 0 && (
                                        <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                            {sub.point_evaluation.pageScores.map((p) => (
                                                <span key={p.key} className={`text-xs rounded-full ${getPointScoreColor(p.score, p.maxScore)} ${getPointScoreTextColor(p.score, p.maxScore)}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '3px 10px' }}>
                                                    <span style={{ opacity: 0.9, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatPageTitle(p.title)}</span>
                                                    <span className="font-semibold" style={{ flexShrink: 0 }}>{p.score}/{p.maxScore}</span>
                                                </span>
                                            ))}
                                        </div>
                                    )}

                                    <div className="text-xs text-slate-500 dark:text-slate-400" style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 8px' }}>
                                        <span>{new Date(sub.submittedAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}, {new Date(sub.submittedAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr</span>
                                        <span className="text-slate-300 dark:text-slate-600">·</span>
                                        <span>Von {userDisplayName(questionnaires[sub.questionnaireId]?.createdBy)}</span>
                                    </div>

                                    <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }} onClick={(e) => e.stopPropagation()}>
                                        <span className="text-xs text-slate-500 dark:text-slate-400">Zuständig:</span>
                                        {getUserColor(effectiveAssignee(sub), userColors) && (
                                            <span style={{ flexShrink: 0, width: 10, height: 10, borderRadius: 9999, backgroundColor: getUserColor(effectiveAssignee(sub), userColors) }} />
                                        )}
                                        <select
                                            value={effectiveAssignee(sub)?.id || ''}
                                            onChange={(e) => { e.stopPropagation(); handleAssignSubmission(sub.id, e.target.value); }}
                                            className="text-xs text-slate-700 dark:text-slate-200 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700"
                                            style={{ padding: '4px 8px' }}
                                        >
                                            <option value="">—</option>
                                            {accounts.map((account) => (<option key={account.id} value={account.id}>{userDisplayName(account)}</option>))}
                                        </select>
                                    </div>

                                    {/* Note as a full-width tappable row. Uses a
                                        div (role=button), not a <button>, because
                                        NotesFeedPreview renders its own mehr/weniger
                                        <button> — nested buttons are invalid DOM. */}
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        onClick={(e) => { e.stopPropagation(); openNoteEditor(sub); }}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); openNoteEditor(sub); } }}
                                        className="w-full text-left cursor-pointer rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50"
                                        style={{ marginTop: '10px', padding: '10px 12px', display: 'flex', alignItems: 'center', gap: '8px' }}
                                    >
                                        {Array.isArray(sub.noteEntries) && sub.noteEntries.length > 0 ? (
                                            <>
                                                <div style={{ minWidth: 0, flex: 1 }}><NotesFeedPreview entries={sub.noteEntries} /></div>
                                                <span className="text-xs font-medium text-green-700 dark:text-green-400" style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                    <StickyNotePlus className="w-4 h-4" />
                                                    Notiz
                                                </span>
                                            </>
                                        ) : (
                                            <>
                                                <StickyNotePlus className="w-4 h-4 text-slate-400 dark:text-slate-500" style={{ flexShrink: 0 }} />
                                                <span className="text-sm text-slate-400 dark:text-slate-500">Notiz hinzufügen …</span>
                                            </>
                                        )}
                                    </div>

                                    {/* Status as a full-width native select (mobile picker) */}
                                    <select
                                        value={sub.status || 'Unbearbeitet'}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => { e.stopPropagation(); updateSubmissionStatus(sub.id, e.target.value); }}
                                        className={`w-full rounded-xl text-sm font-semibold border ${getStatusColor(sub.status || 'Unbearbeitet')}`}
                                        style={{ marginTop: '10px', padding: '10px 12px' }}
                                    >
                                        {statusChoices(sub.status || 'Unbearbeitet').map(({ value, label, selectable }) => (
                                            <option key={value} value={value} disabled={!selectable}>{label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div onClick={() => handleSubmissionClick(sub)} className="results-grid ml-8 flex flex-col gap-3">
                                    {/* Left column: applicant name and questionnaire */}
                                    <div className="flex flex-col gap-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <p className="font-semibold text-green-700 text-base sm:text-lg">{sub.applicantName}</p>
                                            {/* Status badges */}
                                            {(() => {
                                                const submissionDate = new Date(sub.submittedAt);
                                                const isRecent = (Date.now() - submissionDate.getTime()) < 24 * 60 * 60 * 1000; // 24 hours
                                                const isViewed = viewedSubmissions.has(sub.id);
                                                
                                                return (
                                                    <div className="flex items-center gap-1">
                                                        {isRecent && !isViewed && (
                                                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300 rounded-full">
                                                                Neu
                                                            </span>
                                                        )}
                                                        <RepeatApplicantBadge
                                                            submission={sub}
                                                            related={relatedBySubmission[sub.id]}
                                                            onOpen={(id) => handleSubmissionClick({ id })}
                                                        />
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                        <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{questionnaires[sub.questionnaireId]?.title || 'Unbekannter Fragebogen'}</p>
                                        {questionnaires[sub.questionnaireId]?.templateName && (
                                            <p className="text-xs text-slate-400 dark:text-slate-500">
                                                <span>Vorlage: </span>
                                                <span className="text-slate-600 dark:text-slate-300">{questionnaires[sub.questionnaireId].templateName}</span>
                                            </p>
                                        )}
                                        {jobTitlesBySubmission[sub.id] && (
                                            <p className="text-sm text-slate-700 dark:text-slate-200">
                                                <span className="text-slate-400 dark:text-slate-500">Stelle: </span>
                                                <span className="font-medium">{jobTitlesBySubmission[sub.id]}</span>
                                            </p>
                                        )}
                                    </div>

                                    {/* Column: creator + assignee (questionnaire metadata) */}
                                    <div className="flex flex-col items-start sm:items-center justify-center gap-1 text-xs text-slate-400 dark:text-slate-500 min-w-0">
                                        <span>Erstellt von: <span className="text-slate-600 dark:text-slate-300">{userDisplayName(questionnaires[sub.questionnaireId]?.createdBy)}</span></span>
                                        <label className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                            <span>Zuständig:</span>
                                            {getUserColor(effectiveAssignee(sub), userColors) && (
                                                <span
                                                    className="inline-block shrink-0 rounded-full border border-white dark:border-slate-800 shadow-xs"
                                                    style={{ width: 10, height: 10, backgroundColor: getUserColor(effectiveAssignee(sub), userColors) }}
                                                    title={userDisplayName(effectiveAssignee(sub))}
                                                />
                                            )}
                                            <select
                                                value={effectiveAssignee(sub)?.id || ''}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={(e) => { e.stopPropagation(); handleAssignSubmission(sub.id, e.target.value); }}
                                                className="px-1.5 py-0.5 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs"
                                            >
                                                <option value="">—</option>
                                                {accounts.map((account) => (
                                                    <option key={account.id} value={account.id}>{userDisplayName(account)}</option>
                                                ))}
                                            </select>
                                        </label>
                                    </div>

                                    {/* Second column: individual submission date */}
                                    <div className="flex flex-col items-start sm:items-center justify-center min-w-0">
                                        <div className="text-xs sm:text-sm text-slate-400 sm:text-center">
                                            <div>{new Date(sub.submittedAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}</div>
                                            <div>{new Date(sub.submittedAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr</div>
                                        </div>
                                    </div>
                                    
                                    {/* Third column: score display */}
                                    <div className="flex flex-col items-start sm:items-center justify-center min-w-0">
                                        <div className="flex flex-col gap-1">
                                            {/* Point Score — the single score, summed from the answers */}
                                            {sub.point_evaluation && sub.point_evaluation.totalScore !== undefined && (
                                                <div className="flex items-center gap-1">
                                                    <span className="text-xs text-slate-500 dark:text-slate-400">Punkte:</span>
                                                    <span className={`inline-flex items-center justify-center px-2 py-1 text-xs font-bold rounded-full min-w-8 h-6 ${getPointScoreColor(sub.point_evaluation.totalScore, sub.point_evaluation.maxScore)} ${getPointScoreTextColor(sub.point_evaluation.totalScore, sub.point_evaluation.maxScore)}`}>
                                                        {sub.point_evaluation.totalScore}/{sub.point_evaluation.maxScore}
                                                    </span>
                                                </div>
                                            )}
                                            {/* Per-page sub-points as compact chips (sum to the total) */}
                                            {Array.isArray(sub.point_evaluation?.pageScores) && sub.point_evaluation.pageScores.length > 0 && (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px', width: '100%', maxWidth: '22rem' }}>
                                                    {sub.point_evaluation.pageScores.map((p) => (
                                                        <span key={p.key} title={formatPageTitle(p.title)} className={`text-xs rounded-full ${getPointScoreColor(p.score, p.maxScore)} ${getPointScoreTextColor(p.score, p.maxScore)}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '3px 10px' }}>
                                                            <span style={{ opacity: 0.9, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatPageTitle(p.title)}</span>
                                                            <span className="font-semibold" style={{ flexShrink: 0 }}>{p.score}/{p.maxScore}</span>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Column: note — the note block self-centers (mx-auto, fixed
                                        max-width) so the mehr/weniger toggle doesn't shift on expand;
                                        the button is centered below. */}
                                    <div className="min-w-0" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '10px', alignSelf: 'stretch', padding: '4px 8px' }}>
                                        <div className="min-w-0">
                                            <NotesFeedPreview entries={sub.noteEntries} />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); openNoteEditor(sub); }}
                                            style={{ alignSelf: 'center', padding: '6px 12px' }}
                                            className="inline-flex items-center gap-1 rounded-lg border border-slate-300 dark:border-slate-600 text-xs font-medium text-slate-600 dark:text-slate-300 hover:text-green-700 dark:hover:text-green-400 hover:border-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                                            aria-label="Notiz hinzufügen"
                                            title="Notiz hinzufügen"
                                        >
                                            <StickyNotePlus className="w-4 h-4" />
                                            Notiz
                                        </button>
                                    </div>

                                    {/* Right column: Status */}
                                    <div className="flex items-start sm:items-center sm:justify-center min-w-0">
                                        <StatusDropdown
                                            status={sub.status || 'Unbearbeitet'} 
                                            onStatusChange={updateSubmissionStatus}
                                            submissionId={sub.id}
                                        />
                                    </div>
                                </div>
                                
                      </li>
                    )) : (
                      <li className="p-8 text-center text-slate-500 dark:text-slate-400">
                        {loadingData ? (
                          <div className="flex flex-col items-center gap-3">
                            <svg className="w-10 h-10 animate-spin text-slate-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Bewerbungen werden geladen...</p>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-3">
                            <svg className="w-12 h-12 text-slate-300 dark:text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <div>
                              <p className="text-lg font-medium text-slate-600 dark:text-slate-300">
                                {submissions.length === 0 ? 'Noch keine Antworten' : 'Keine Ergebnisse gefunden'}
                              </p>
                              <p className="text-sm text-slate-500 dark:text-slate-400">
                                {submissions.length === 0
                                  ? 'Erstellen Sie einen Fragebogen und teilen Sie den Link mit Bewerbern.'
                                  : 'Versuchen Sie andere Filtereinstellungen oder entfernen Sie Filter.'
                                }
                              </p>
                            </div>
                          </div>
                        )}
                      </li>
                    );
                    })()}
                  </ul>

                    {/* Pagination footer */}
                    {pageInfo.pageCount > 1 && (
                      <div className="p-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50">
                        <Pagination
                          currentPage={pageInfo.page}
                          pageCount={pageInfo.pageCount}
                          from={pageInfo.from}
                          to={pageInfo.to}
                          total={pageInfo.total}
                          onPageChange={setCurrentPage}
                        />
                      </div>
                    )}
                </div>
            </div>
            
            {/* Delete Confirmation Modal for Overview */}
            {showDeleteModal && submissionToDelete && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-slate-800 dark:text-slate-200 p-6 rounded-2xl shadow-2xl max-w-md w-full m-4 transform transition-all duration-300">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="shrink-0">
                                <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                            </div>
                            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">Ergebnis löschen?</h2>
                        </div>
                        <p className="text-slate-600 dark:text-slate-300 mb-6">
                            Möchten Sie dieses Ergebnis wirklich löschen?
                            <br /><br />
                            Diese Aktion kann nicht rückgängig gemacht werden.
                        </p>
                        <div className="flex gap-3">
                            <button 
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setSubmissionToDelete(null);
                                }} 
                                className="flex-1 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                            >
                                Abbrechen
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                            >
                                Löschen
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {noteEditSub && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={closeNoteEditor}>
                    <div className="bg-white dark:bg-slate-800 dark:text-slate-200 p-6 rounded-2xl shadow-2xl max-w-lg w-full m-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-3 mb-4">
                            <svg className="w-7 h-7 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">Notiz bearbeiten</h2>
                        </div>
                        <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
                            {(noteEditSub?.noteEntries || []).length === 0 && (
                                <p className="text-sm italic text-slate-400 dark:text-slate-500">Noch keine Notizen.</p>
                            )}
                            {(noteEditSub?.noteEntries || []).map((e) => (
                                <div key={e.id} className="border border-slate-200 dark:border-slate-700 rounded-lg p-2">
                                    {noteByline(e) && (
                                        <div className="text-[11px] text-slate-400 dark:text-slate-500 mb-1">{noteByline(e)}</div>
                                    )}
                                    {noteEditingId === e.id ? (
                                        <div className="space-y-2">
                                            <textarea
                                                value={noteEditingText}
                                                onChange={(ev) => setNoteEditingText(ev.target.value)}
                                                className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 text-sm min-h-[70px]"
                                            />
                                            <div className="flex gap-2">
                                                <button type="button" disabled={noteSaving} onClick={handleSaveNoteEdit} className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50">Speichern</button>
                                                <button type="button" onClick={() => { setNoteEditingId(null); setNoteEditingText(''); }} className="px-3 py-1 text-sm bg-slate-400 text-white rounded hover:bg-slate-500">Abbrechen</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap wrap-break-word">{e.text}</p>
                                            <div className="flex gap-3 mt-1">
                                                <button type="button" onClick={() => { setNoteEditingId(e.id); setNoteEditingText(e.text); }} className="text-xs text-blue-600 hover:underline">Bearbeiten</button>
                                                <button type="button" disabled={noteSaving} onClick={() => handleDeleteNote(e.id)} className="text-xs text-red-600 hover:underline disabled:opacity-50">Löschen</button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className="mt-3 space-y-2">
                            <textarea
                                value={noteDraft}
                                onChange={(e) => setNoteDraft(e.target.value)}
                                placeholder="Neuen Post hinzufügen…"
                                className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 text-sm min-h-[70px]"
                            />
                            <div className="flex justify-end gap-2">
                                <button type="button" onClick={closeNoteEditor} className="px-3 py-1 text-sm bg-slate-400 text-white rounded hover:bg-slate-500">Schließen</button>
                                <button type="button" disabled={noteSaving || !noteDraft.trim()} onClick={handleAddNote} className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50">Hinzufügen</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Helper function to calculate question number with sub-numbering for conditional questions
const getQuestionNumber = (question, labelMap) => {
  if (!question) return '?';
  return labelMap[question.id] || '?';
};

/**
 * Was der Fragebogen 2 beim Absenden an uns zurückgemeldet hat, in zwei Teilen:
 * die Kennzahlen stehen bei seinem Knopf, die Antworten im Antworten-Reiter.
 *
 * Fragebogen 2 ist ein fertiges HTML-Dokument und kennt keine gewichteten
 * Punkte — was hier als Bewertung steht, rechnet das Dokument selbst: Rätsel
 * richtig oder falsch, Radar-Selbsteinschätzung, echte Vorteile gegen
 * Trugschlüsse, Vollständigkeit. Mehr gibt es dort nicht herzuholen.
 */
/** Die beiden Fragebögen über den Antworten. */
const FIRST_TAB = 'fragebogen1';
const ASSESSMENT_TAB = 'fragebogen2';

/**
 * Der Punkt am Reiter "Fragebogen 2": ob dort überhaupt etwas liegt.
 *
 * Nur die Farbe, kein Wort dahinter — der Zustand im Klartext steht im Reiter
 * selbst, und eine Reiterzeile, die ihn mitträgt, liest sich nicht mehr als
 * Reiterzeile. Dieselben Farben wie der Statuspunkt der Stellenkarte.
 *
 * Für den Fragebogen 1 gibt es keinen: ihn gibt es immer, sonst gäbe es diese
 * Seite nicht. Ein Punkt, der nie seine Farbe wechselt, sagt nichts.
 */
function assessmentDot(invite) {
    if (!invite) {
        return { className: 'border border-slate-300 dark:border-slate-500', title: 'Noch nicht verschickt' };
    }
    if (inviteActivity(invite).state === 'submitted') {
        return { className: 'bg-emerald-500', title: 'Abgeschickt' };
    }
    return { className: 'bg-amber-500', title: 'Verschickt, noch nicht abgeschickt' };
}

function AssessmentStats({ result }) {
    const stats = [
        result?.progress != null && { label: 'Fortschritt', value: `${result.progress} %` },
        result?.questionCount && { label: 'Beantwortet', value: `${result.answeredCount}/${result.questionCount}` },
        result?.puzzleCount > 0 && { label: 'Rätsel richtig', value: `${result.puzzlesSolved}/${result.puzzleCount}` },
        result?.ratingAverage != null && { label: 'Radar ⌀', value: `${result.ratingAverage}/10` },
        result?.genuineCount + result?.decoyCount > 0 && {
            label: 'Echt / Trugschluss',
            value: `${result.genuineCount} / ${result.decoyCount}`,
        },
    ].filter(Boolean);

    if (!stats.length) return null;

    return (
        <div className="flex flex-wrap gap-2">
            {stats.map((stat) => (
                <div key={stat.label} className="px-3 py-1.5 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg">
                    <span className="text-xs text-slate-500 dark:text-slate-400">{stat.label}</span>
                    <span className="ml-2 font-mono text-sm font-bold text-slate-700 dark:text-slate-200">{stat.value}</span>
                </div>
            ))}
        </div>
    );
}

/**
 * Die einzelnen Antworten des Fragebogens 2.
 *
 * Steht im Antworten-Reiter neben den Seiten des ersten Fragebogens, nicht in
 * der Spalte daneben: es sind rund 37 Einträge, die dort alles andere aus dem
 * Bild geschoben haben.
 */
function AssessmentAnswerList({ answers }) {
    return (
        <div className="space-y-2">
            {answers.map((entry, index) => {
                const empty = entry.answer === null || entry.answer === undefined;
                return (
                    <div key={`${entry.label}-${index}`} className="p-3 bg-slate-50 dark:bg-slate-700/40 border border-slate-200 dark:border-slate-600 rounded-lg">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                            <p className="font-semibold text-sm text-slate-700 dark:text-slate-200">{entry.label}</p>
                            {entry.correct === true && (
                                <span className="text-xs px-1.5 py-0.5 rounded font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">richtig</span>
                            )}
                            {entry.correct === false && (
                                <span className="text-xs px-1.5 py-0.5 rounded font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                                    falsch · richtig wäre: {entry.expected}
                                </span>
                            )}
                            {typeof entry.genuineCount === 'number' && (
                                <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-slate-200 text-slate-700 dark:bg-slate-600 dark:text-slate-200">
                                    {entry.genuineCount} echt · {entry.decoyCount} Trugschluss
                                </span>
                            )}
                            {entry.max && (
                                <span className="text-xs px-1.5 py-0.5 rounded font-mono font-medium bg-slate-200 text-slate-700 dark:bg-slate-600 dark:text-slate-200">
                                    {entry.value ?? '–'}/{entry.max}
                                </span>
                            )}
                        </div>
                        {empty ? (
                            <p className="text-sm italic text-slate-400 dark:text-slate-500">Keine Antwort</p>
                        ) : Array.isArray(entry.answer) ? (
                            <ul className="text-sm text-slate-600 dark:text-slate-300 list-disc list-inside space-y-0.5">
                                {entry.answer.map((option) => <li key={option}>{option}</li>)}
                            </ul>
                        ) : (
                            <p className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap wrap-break-word">{entry.answer}</p>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

/**
 * Der Fragebogen 2 dieser Bewerbung: wo er steht, der Weg ihn zu verschicken,
 * und was zurückkam.
 *
 * Steht neben dem ersten Fragebogen unter "Eingereichte Antworten", weil beides
 * dasselbe ist — eingereichte Antworten derselben Person. In der Spalte daneben
 * hat es zu viel Platz genommen.
 *
 * Liest denselben Stand wie der Einladungsdialog unter Stellenanzeigen
 * (inviteActivity), damit "inaktiv seit 7 Tagen" an beiden Orten dasselbe heißt.
 */
function AssessmentPanel({ invites, loading, error, onSend }) {
    const newest = invites[0] || null;
    const activity = newest ? inviteActivity(newest) : null;

    const line = (() => {
        if (loading) return { text: 'wird geladen …', tone: 'text-slate-500 dark:text-slate-400' };
        // "Ich weiß es nicht" darf nie als "noch nicht verschickt" ankommen —
        // genau diese Karte entscheidet, ob jemand (erneut) sendet.
        if (error) return { text: error, tone: 'text-red-700 dark:text-red-300' };
        if (!newest) return { text: 'noch nicht verschickt', tone: 'text-amber-700 dark:text-amber-400' };
        switch (activity.state) {
            case 'submitted':
                return { text: 'abgeschickt', tone: 'text-emerald-700 dark:text-emerald-400' };
            case 'inactive':
                return {
                    text: `seit ${activity.idleDays} Tagen inaktiv · ${newest.progress} %`,
                    tone: 'text-amber-700 dark:text-amber-400',
                };
            case 'in_progress':
                return {
                    text: `in Arbeit · ${newest.progress} % · zuletzt aktiv ${formatSince(newest.lastActiveAt || newest.openedAt)}`,
                    tone: 'text-slate-600 dark:text-slate-300',
                };
            case 'opened':
                return { text: 'geöffnet', tone: 'text-slate-600 dark:text-slate-300' };
            default: {
                const sent = newest.sentAt || newest.createdAt;
                const on = sent ? new Date(sent).toLocaleDateString('de-DE') : null;
                return {
                    text: on ? `verschickt am ${on}, noch nicht geöffnet` : 'verschickt, noch nicht geöffnet',
                    tone: 'text-slate-600 dark:text-slate-300',
                };
            }
        }
    })();

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Stand der Einladung</p>
                <p className={`text-sm font-medium ${line.tone}`}>{line.text}</p>
            </div>

            <button
                type="button"
                onClick={onSend}
                className="w-full flex items-center justify-center gap-2 px-5 py-4 text-base font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-sm transition-colors"
            >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {newest ? 'Fragebogen 2 erneut senden' : 'Fragebogen 2 senden'}
            </button>

            {newest?.result && <AssessmentStats result={newest.result} />}

            {newest?.answers?.length > 0 ? (
                <AssessmentAnswerList answers={newest.answers} />
            ) : (
                <p className="text-sm text-slate-500 dark:text-slate-400 italic">
                    {newest
                        ? 'Noch keine Antworten eingegangen — sie erscheinen hier, sobald der Fragebogen abgeschickt wurde.'
                        : 'Noch nicht verschickt. Nach dem Absenden stehen die Antworten hier.'}
                </p>
            )}
        </div>
    );
}

// --- Detailansicht einer Antwort mit KI-Analyse ---
/**
 * Sagt, wenn an einer Bewerbung etwas nicht stimmt, das man sonst nicht sieht.
 *
 * Das Formular schreibt seit jeher mit, wie eine Bewerbung zustande kam
 * (`submission_type`) und wie viele Fragen es beim Absenden als offen zählte
 * (`completion_summary`) — gelesen hat beides nie jemand. Eine Bewerbung, die
 * der Timer abgeschickt hat, sah deshalb aus wie eine, die jemand bewusst mit
 * neun unbeantworteten Fragen eingereicht hat, inklusive roter Flaggen darauf.
 *
 * Bei einer vollständigen, normal abgeschickten Bewerbung erscheint hier
 * nichts.
 */
/**
 * Sagt bei jeder Frage, ob sie beantwortet werden musste.
 *
 * Ohne das liest sich "Keine Antwort" mehrdeutig: verweigert, verlorengegangen
 * oder schlicht nicht verlangt? Bei `required === false` prüft das Formular die
 * Frage beim Absenden gar nicht erst (siehe validateCustomQuestionList in
 * StandaloneApplicantForm.js) — sie war ein Angebot.
 *
 * Beides wird beschriftet, nicht nur die Ausnahme: ein fehlendes Kennzeichen
 * wäre sonst nicht von einer fehlenden Angabe zu unterscheiden. "Pflicht" bleibt
 * deshalb bewusst leise, "Freiwillig" trägt die Information.
 */
function QuestionObligationBadge({ question }) {
    if (question?.required === false) {
        return (
            <span
                className="text-xs px-1.5 py-0.5 rounded font-medium bg-slate-200 text-slate-600 dark:bg-slate-600 dark:text-slate-200 whitespace-nowrap"
                title="Freiwillige Frage — sie musste nicht beantwortet werden"
            >
                Freiwillig
            </span>
        );
    }
    return (
        <span
            className="text-xs text-slate-400 dark:text-slate-500 whitespace-nowrap"
            title="Pflichtfrage — ohne Antwort kommt das Formular nicht weiter"
        >
            Pflicht
        </span>
    );
}

function SubmissionIntegrityNotice({ submission, recoveredCount = 0 }) {
    const summary = submission?.completionSummary;
    const open = Number(summary?.unansweredQuestions) || 0;
    const type = submission?.submissionType || 'normal';

    const notes = [
        type === 'auto-submit' && {
            key: 'auto',
            text: 'Die Zeit lief ab — der Fragebogen wurde automatisch abgeschickt, so wie er in dem Moment war.'
        },
        type === 'withdrawal' && {
            key: 'withdrawal',
            text: 'Der Bewerber hat die Bewerbung selbst abgebrochen.'
        },
        open > 0 && {
            key: 'open',
            text: `${open} von ${summary.totalQuestions} Fragen waren beim Absenden offen (${summary.completionPercentage} % ausgefüllt).`
        },
        recoveredCount > 0 && {
            key: 'recovered',
            text: `${recoveredCount} Antworten gehören zu Fragen, die der Fragebogen nicht mehr enthält — sie stehen im Reiter "Nicht mehr im Fragebogen".`
        }
    ].filter(Boolean);

    if (!notes.length) return null;

    return (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-xl p-4">
            <p className="font-semibold text-sm text-amber-800 dark:text-amber-300 mb-1">Zu dieser Bewerbung</p>
            <ul className="space-y-1">
                {notes.map((note) => (
                    <li key={note.key} className="text-sm text-amber-700 dark:text-amber-400">{note.text}</li>
                ))}
            </ul>
        </div>
    );
}

function SubmissionDetail({ submission, relatedApplications = [], onOpenSubmission, submissionFileUrls = {}, accounts = [], recommendationTiers = null, onAssignSubmission, onStatusChange, onInvitationSent, onSetUpJob, onBack, onUpdate, onDelete }) {
    const [questionnaire, setQuestionnaire] = useState(null);
    // Track the questionnaire fetch separately so we don't flash the
    // "Fragebogen nicht gefunden" screen before the fetch resolves.
    const [loadingQuestionnaire, setLoadingQuestionnaire] = useState(!!submission.questionnaireId);
    const [isLoading, setIsLoading] = useState(false);
    const [pipelineProgress, setPipelineProgress] = useState(null);
    const [error, setError] = useState(null);
    const [analysis, setAnalysis] = useState(submission.analysis || null);
    const [showReevaluateConfirm, setShowReevaluateConfirm] = useState(false);
    const [noteEntries, setNoteEntries] = useState(() =>
        Array.isArray(submission.noteEntries) ? submission.noteEntries : migrateNotes(submission.analysis)
    );
    const [newPost, setNewPost] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editingText, setEditingText] = useState('');
    useEffect(() => {
        setNoteEntries(Array.isArray(submission.noteEntries) ? submission.noteEntries : migrateNotes(submission.analysis));
        // Re-sync only when a different submission is opened. Adding submission.analysis or
        // submission.noteEntries here would re-run on every refresh of the same submission and
        // overwrite notes the user is currently editing.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submission.id]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [activeTab, setActiveTab] = useState('section_default');
    /** Welcher der beiden Fragebögen gezeigt wird — eine Ebene über den Seiten. */
    const [questionnaireTab, setQuestionnaireTab] = useState(FIRST_TAB);
    const [eligibilityScore, setEligibilityScore] = useState(
        getQualificationScoreFromData(submission.qualification_evaluation, submission.point_evaluation)
    );
    const [questionEvaluations, setQuestionEvaluations] = useState(submission.qualification_evaluation?.questionEvaluations || []);
    const [notification, setNotification] = useState(null);
    const [pointEvaluation, setPointEvaluation] = useState(submission.point_evaluation || null);
    const [editingPointQuestionId, setEditingPointQuestionId] = useState(null);
    const [editingPointValue, setEditingPointValue] = useState(null);
    const [editingAdjustment, setEditingAdjustment] = useState(false);
    const [editedAdjustment, setEditedAdjustment] = useState(null);
    const [analysisCardTab, setAnalysisCardTab] = useState('analysis');
    const [invites, setInvites] = useState([]);
    const [loadingInvites, setLoadingInvites] = useState(true);
    const [inviteError, setInviteError] = useState(null);
    const [showSendAssessment, setShowSendAssessment] = useState(false);

    const loadInvites = useCallback(async () => {
        setLoadingInvites(true);
        const result = await inviteService.listForApplicant({
            submissionId: submission.id,
            email: contactKeysFromAnswers(submission.answers).emails[0] || '',
        });
        setInvites(result.data || []);
        setInviteError(
            result.success ? null : result.error || 'Der Stand des Fragebogens 2 ließ sich nicht laden.'
        );
        setLoadingInvites(false);
    }, [submission.id, submission.answers]);

    useEffect(() => {
        loadInvites();
    }, [loadInvites]);

    /** Nur an der ersten Antwort mit Adresse — sonst steht der Knopf mehrfach da. */
    const firstEmailQuestionId = useMemo(() => {
        const hit = Object.entries(submission.answers || {}).find(
            ([, answer]) => detectRedactablePii(flattenAnswerText(answer)).email
        );
        return hit ? hit[0] : null;
    }, [submission.answers]);

    const defaultQuestions = useMemo(
        () => (Array.isArray(questionnaire?.defaultQuestions) ? questionnaire.defaultQuestions : []),
        [questionnaire?.defaultQuestions]
    );
    const qualificationMaxScore = useMemo(() => {
        const fallbackFromDefaults = defaultQuestions.filter((question) => question.id !== 'system_name').length;
        return getQualificationMaxScoreFromData(
            { score: eligibilityScore, questionEvaluations },
            pointEvaluation,
            fallbackFromDefaults
        );
    }, [defaultQuestions, eligibilityScore, pointEvaluation, questionEvaluations]);
    const totalPointTotals = useMemo(
        () => getTotalPointTotals(pointEvaluation),
        [pointEvaluation]
    );
    const normalizedQuestionnaire = useMemo(
        () => normalizeQuestionnaireData(questionnaire || {}),
        [questionnaire]
    );
    const customQuestionPages = useMemo(() => {
        const pages = normalizedQuestionnaire?.questionPages || [];
        return pages.filter((page) => Array.isArray(page?.questions) && page.questions.length > 0);
    }, [normalizedQuestionnaire]);
    // Antworten, deren Frage der Fragebogen nicht mehr kennt. Ohne das hier
    // blieben sie unsichtbar: die Ansicht läuft über die Fragen, nicht über die
    // Antworten, und eine Frage, die es nicht mehr gibt, holt ihre Antwort nie
    // ab. Siehe utils/submissionAnswerRecovery.js.
    const recoveredQuestions = useMemo(
        () => recoverOrphanAnswers(submission, collectKnownQuestionIds(defaultQuestions, customQuestionPages)),
        [submission, defaultQuestions, customQuestionPages]
    );
    const defaultSectionLabel = useMemo(() => {
        const configuredLabel = String((questionnaire?.intro_page?.defaultSectionTitle ?? questionnaire?.defaultSectionTitle ?? '') || '').trim();
        if (!configuredLabel || configuredLabel === LEGACY_DEFAULT_SECTION_LABEL) {
            return 'Formale Fragen';
        }
        return configuredLabel;
    }, [questionnaire?.intro_page?.defaultSectionTitle, questionnaire?.defaultSectionTitle]);
    const defaultQuestionsPageOrder = useMemo(
        () => clampDefaultQuestionsPageOrder(
            questionnaire?.defaultQuestionsPageOrder ?? questionnaire?.intro_page?.defaultQuestionsPageOrder,
            customQuestionPages.length
        ),
        [questionnaire?.defaultQuestionsPageOrder, questionnaire?.intro_page?.defaultQuestionsPageOrder, customQuestionPages.length]
    );
    const orderedQuestionSections = useMemo(() => {
        const sections = [];
        const total = customQuestionPages.length + 1;
        let pageCursor = 0;

        for (let position = 1; position <= total; position += 1) {
            if (position === defaultQuestionsPageOrder) {
                sections.push({
                    id: 'section_default',
                    type: 'default',
                    label: defaultSectionLabel,
                    questions: defaultQuestions
                });
                continue;
            }

            const page = customQuestionPages[pageCursor];
            if (page) {
                sections.push({
                    id: `section_page_${page.id || pageCursor}`,
                    type: 'custom',
                    label: String(page?.title || '').trim() || `Seite ${pageCursor + 1}`,
                    pageId: page.id,
                    questions: page.questions || []
                });
            }
            pageCursor += 1;
        }

        if (recoveredQuestions.length) {
            sections.push({
                id: 'section_recovered',
                type: 'custom',
                label: 'Nicht mehr im Fragebogen',
                questions: recoveredQuestions,
                recovered: true
            });
        }

        return sections;
    }, [customQuestionPages, defaultQuestionsPageOrder, defaultSectionLabel, defaultQuestions, recoveredQuestions]);
    const answerTabs = useMemo(
        () => orderedQuestionSections.map((section) => ({
            id: section.id,
            label: section.label
        })),
        [orderedQuestionSections]
    );
    const activeSection = useMemo(
        () => {
            // Beim Fragebogen 2 gibt es keine Seiten des ersten — ohne das hier
            // fiele der Reiter auf dessen erste Seite zurück und zeigte beides.
            if (questionnaireTab === ASSESSMENT_TAB) return null;
            return orderedQuestionSections.find((section) => section.id === activeTab) || orderedQuestionSections[0] || null;
        },
        [orderedQuestionSections, activeTab, questionnaireTab]
    );

    useEffect(() => {
        if (!answerTabs.some((tab) => tab.id === activeTab)) {
            setActiveTab(answerTabs[0]?.id || 'section_default');
        }
    }, [answerTabs, activeTab]);

    // Jump from the Übersicht score list to the submitted answer: open the
    // answer tab containing the question, then scroll to and highlight it.
    // The scroll runs in an effect so the tab content is in the DOM first.
    const [answerScrollTarget, setAnswerScrollTarget] = useState(null);
    const jumpToAnswer = useCallback((questionId) => {
        const section = orderedQuestionSections.find((s) =>
            (s.questions || []).some((q) => String(q?.id) === String(questionId)));
        if (!section) return;
        setQuestionnaireTab(FIRST_TAB);
        setActiveTab(section.id);
        setAnswerScrollTarget({ questionId, requestedAt: Date.now() });
    }, [orderedQuestionSections]);

    useEffect(() => {
        if (!answerScrollTarget) return undefined;
        const frame = requestAnimationFrame(() => {
            const selector = `[data-answer-question-id="${CSS.escape(String(answerScrollTarget.questionId))}"]`;
            const el = document.querySelector(selector);
            if (!el) return;
            // Offset the sticky top nav so the question text lands below it,
            // not hidden behind it.
            const nav = document.querySelector('nav');
            el.style.scrollMarginTop = `${(nav?.offsetHeight || 0) + 16}px`;
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            // Inline styles: the prebuilt output.css has no plain ring utilities.
            el.style.outline = '2px solid #4ade80';
            el.style.outlineOffset = '6px';
            el.style.borderRadius = '0.5rem';
            setTimeout(() => {
                el.style.outline = '';
                el.style.outlineOffset = '';
                el.style.borderRadius = '';
                el.style.scrollMarginTop = '';
            }, 1800);
        });
        return () => cancelAnimationFrame(frame);
    }, [answerScrollTarget]);

    // Load questionnaire data
    useEffect(() => {
        const loadQuestionnaire = async () => {
            setLoadingQuestionnaire(true);
            try {
                const q = await dbService.getQuestionnaire(submission.questionnaireId);
                setQuestionnaire(q);
            } catch (error) {
                console.error('Error loading questionnaire:', error);
                setError('Fragebogen konnte nicht geladen werden');
            } finally {
                setLoadingQuestionnaire(false);
            }
        };

        if (submission.questionnaireId) {
            loadQuestionnaire();
        } else {
            setLoadingQuestionnaire(false);
        }
    }, [submission.questionnaireId]);

    // Print function for single submission
    const printSubmission = () => {
        const printWindow = window.open('', '_blank');
        const printContent = generateSinglePrintContent();
        
        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Ergebnis: ${submission.applicantName}</title>
                <style>
                    body { font-family: Arial, sans-serif; margin: 20px; }
                    .header { text-align: center; margin-bottom: 30px; }
                    .scoring-section { margin: 20px 0; padding: 15px; border: 2px solid #333; border-radius: 10px; }
                    .submission { margin-bottom: 40px; border: 1px solid #ccc; padding: 20px; }
                    .submission-header { border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
                    .question { margin-bottom: 15px; }
                    .question-text { font-weight: bold; margin-bottom: 5px; }
                    .question-description { font-size: 12px; color: #64748b; margin-bottom: 5px; }
                    .answer { border: 1px solid #ddd; padding: 10px; border-radius: 5px; }
                    .analysis { border: 1px solid #4ade80; padding: 15px; border-radius: 5px; margin-top: 20px; }
                    .score { font-size: 18px; font-weight: bold; color: #2d5a2d; }
                    .notes { border: 1px solid #fbbf24; padding: 10px; border-radius: 5px; margin-top: 10px; }
                    @media print {
                        .question { page-break-inside: avoid; }
                        .scoring-section { page-break-inside: avoid; }
                        body { -webkit-print-color-adjust: economy; color-adjust: economy; }
                    }
                </style>
            </head>
            <body>
                ${printContent}
            </body>
            </html>
        `);
        
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
    };

    // Generate print content for single submission
    const generateSinglePrintContent = () => {
        // Format duration
        const formatDuration = (duration) => {
            if (!duration) return "0:00";
            if (typeof duration === 'string' && duration.includes(':')) return duration;
            const numDuration = parseInt(duration);
            if (isNaN(numDuration)) return "0:00";
            const minutes = Math.floor(numDuration / 60);
            const seconds = numDuration % 60;
            return `${minutes}:${seconds.toString().padStart(2, '0')}`;
        };

        // Format date
        const formatDate = (dateString) => {
            if (!dateString) return "Unbekannt";
            const date = new Date(dateString);
            return date.toLocaleDateString('de-DE', { 
                day: '2-digit', 
                month: '2-digit', 
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        };

        let content = `
            <div class="header">
                <h1>Ergebnis: ${submission.applicantName}</h1>
                <p>Fragebogen: ${questionnaire?.title || 'Unbekannt'}</p>
                <p>Eingereicht: ${formatDate(submission.submittedAt)}</p>
                <p>Dauer: ${formatDuration(submission.completionDuration)}</p>
                <p>Exportiert am: ${new Date().toLocaleDateString('de-DE')}</p>
            </div>
        `;

        // Add scoring section
        content += `
            <div class="scoring-section" style="margin: 20px 0; padding: 15px; border: 2px solid #333; border-radius: 10px;">
                <h2 style="text-align: center; margin-bottom: 15px; color: #333; font-size: 18px;">Bewertung</h2>
                <div style="display: flex; justify-content: space-around; align-items: center;">
        `;

        // Qualification Score
        const qualificationScore = eligibilityScore || 0;
        const qualificationPercentage = Math.min((qualificationScore / qualificationMaxScore) * 100, 100);
        const submissionPointScore = submission.point_evaluation?.totalScore;
        const submissionPointMaxScore = submission.point_evaluation?.maxScore;
        const hasPointEvaluation = Number.isFinite(Number(submissionPointScore)) && Number.isFinite(Number(submissionPointMaxScore)) && Number(submissionPointMaxScore) > 0;
        const pointScoreValue = hasPointEvaluation ? Number(submissionPointScore) : 0;
        const pointMaxValue = hasPointEvaluation ? Number(submissionPointMaxScore) : 1;
        const pointPercentage = Math.min((pointScoreValue / pointMaxValue) * 100, 100);
        content += `
            <div style="text-align: center;">
                <div style="position: relative; width: 80px; height: 80px; margin: 0 auto 8px;">
                    <svg width="80" height="80" style="transform: rotate(-90deg);">
                        <circle cx="40" cy="40" r="34" stroke="#e5e7eb" stroke-width="6" fill="none"/>
                        <circle cx="40" cy="40" r="34" stroke="#10b981" stroke-width="6" fill="none" 
                                stroke-dasharray="${2 * Math.PI * 34}" 
                                stroke-dashoffset="${2 * Math.PI * 34 - (qualificationPercentage / 100) * (2 * Math.PI * 34)}"
                                stroke-linecap="round"/>
                    </svg>
                    <div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 16px; font-weight: bold; color: #333;">
                        ${qualificationScore}/${qualificationMaxScore}
                    </div>
                </div>
                <div style="font-weight: bold; margin-bottom: 8px; color: #333; font-size: 14px;">Qualifikation</div>
                ${qualificationScore !== null ? 
                    `<div style="font-size: 18px;">${getQualificationEmoji(qualificationScore, qualificationMaxScore)}</div>` : 
                    '<div style="padding: 3px 8px; background: #e5e7eb; border-radius: 12px; font-size: 10px; color: #666;">Noch nicht bewertet</div>'
                }
            </div>
        `;

        // Point Score
        content += `
            <div style="text-align: center;">
                <div style="position: relative; width: 80px; height: 80px; margin: 0 auto 8px;">
                    <svg width="80" height="80" style="transform: rotate(-90deg);">
                        <circle cx="40" cy="40" r="34" stroke="#e5e7eb" stroke-width="6" fill="none"/>
                        <circle cx="40" cy="40" r="34" stroke="#10b981" stroke-width="6" fill="none" 
                                stroke-dasharray="${2 * Math.PI * 34}" 
                                stroke-dashoffset="${2 * Math.PI * 34 - (pointPercentage / 100) * (2 * Math.PI * 34)}"
                                stroke-linecap="round"/>
                    </svg>
                    <div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 16px; font-weight: bold; color: #333;">
                        ${hasPointEvaluation ? `${pointScoreValue}/${pointMaxValue}` : '—'}
                    </div>
                </div>
                <div style="font-weight: bold; margin-bottom: 8px; color: #333; font-size: 14px;">Punkte</div>
                ${hasPointEvaluation ? 
                    `<div style="font-size: 18px;">${getPointScoreEmoji(pointScoreValue, pointMaxValue)}</div>` : 
                    '<div style="padding: 3px 8px; background: #e5e7eb; border-radius: 12px; font-size: 10px; color: #666;">Noch nicht bewertet</div>'
                }
            </div>
        `;

        content += `
                </div>
            </div>
        `;
        
        content += '<div class="submission">';
        
        // Add name first
        content += `
            <div class="question">
                <div class="question-text">Name</div>
                <div class="answer">${submission.applicantName || 'Keine Antwort'}</div>
            </div>
        `;
        
        // Add questions grouped by section order (default + custom pages)
        orderedQuestionSections
            .filter((section) => Array.isArray(section?.questions) && section.questions.length > 0)
            .forEach((section) => {
                content += `
                    <div class="question">
                        <div class="question-text">Abschnitt: ${section.label}</div>
                    </div>
                `;

                section.questions.forEach((q) => {
                    const answer = submission.answers[q.id];
                    let answerHtml;
                    if (Array.isArray(answer) && answer.some(r => r && typeof r === 'object' && ('label' in r || 'value' in r))) {
                        const rows = answer
                            .filter((r) => r && r.value)
                            .map((r) => r.label ? `<div>${r.label}: <strong>${r.value}</strong></div>` : `<div><strong>${r.value}</strong></div>`)
                            .join('');
                        answerHtml = rows || 'Keine Antwort';
                    } else if (q.type === 'ranking' && Array.isArray(answer) && answer.length > 0) {
                        answerHtml = answer.map((item, idx) => `<div>${idx + 1}. ${item}</div>`).join('');
                    } else if (q.type === 'file-upload' && answer && Array.isArray(answer.fileNames) && answer.fileNames.length > 0) {
                        answerHtml = answer.fileNames.join(', ');
                    } else if (Array.isArray(answer)) {
                        answerHtml = answer.length > 0 ? answer.join(', ') : 'Keine Antwort';
                    } else {
                        answerHtml = answer || 'Keine Antwort';
                    }
                    content += `
                        <div class="question">
                            <div class="question-text">${q.text} <span style="font-weight:normal;color:#64748b">(${q.required === false ? 'freiwillig' : 'Pflicht'})</span></div>
                            ${q.description ? `<div class="question-description">${q.description}</div>` : ''}
                            <div class="answer">${answerHtml}</div>
                        </div>
                    `;
                });
            });
        
        // Add analysis
        if (submission.analysis) {
            content += `
                <div class="analysis">
                    <h3>KI-Analyse</h3>
                    ${(() => { const recMax = Number(submission.point_evaluation?.maxScore) || 0; const recPct = recMax > 0 ? Math.round(((Number(submission.point_evaluation?.totalScore) || 0) / recMax) * 100) : 0; const rec = getRecommendation(recPct, recommendationTiers); return `<div style="display:inline-block;margin:4px 0 8px;padding:4px 12px;border-radius:9999px;font-weight:700;color:#fff;background:${rec.color};">Empfehlung: ${rec.label}</div>`; })()}
                    <div class="score">Score: ${submission.point_evaluation ? `${submission.point_evaluation.totalScore}/${submission.point_evaluation.maxScore}` : (submission.analysis.score || 'N/A')}</div>
                    <div class="summary">
                        <h4>Zusammenfassung</h4>
                        <p>${submission.analysis.summary || 'Keine Zusammenfassung verfügbar'}</p>
                    </div>
                    ${submission.analysis.strengths && submission.analysis.strengths.length > 0 ? `
                    <div class="strengths">
                        <h4>Erkannte Stärken</h4>
                        <ul>
                            ${submission.analysis.strengths.map(strength => `<li>${strength}</li>`).join('')}
                        </ul>
                    </div>
                    ` : ''}
                    ${submission.analysis.weaknesses && submission.analysis.weaknesses.length > 0 ? `
                    <div class="weaknesses">
                        <h4>Erkannte Schwächen</h4>
                        <ul>
                            ${submission.analysis.weaknesses.map(weakness => `<li>${weakness}</li>`).join('')}
                        </ul>
                    </div>
                    ` : ''}
                    ${submission.analysis.areasForDiscussion && submission.analysis.areasForDiscussion.length > 0 ? `
                    <div class="discussion">
                        <h4>Themen für das Interview</h4>
                        <ul>
                            ${submission.analysis.areasForDiscussion.map(area => `<li>${area}</li>`).join('')}
                        </ul>
                    </div>
                    ` : ''}
                    ${(() => {
                        const entries = Array.isArray(submission.noteEntries) ? submission.noteEntries : [];
                        if (entries.length) {
                            const items = entries.map((e) =>
                                `<div style="margin-bottom:6px;">${(e.text || '').replace(/</g, '&lt;')}${noteByline(e) ? `<br/><span style="color:#888;font-size:12px;">${noteByline(e)}</span>` : ''}</div>`
                            ).join('');
                            return `<div class="notes"><strong>Notizen:</strong><br/>${items}</div>`;
                        }
                        return submission.analysis?.notes ? `<div class="notes"><strong>Notiz:</strong> ${submission.analysis.notes}</div>` : '';
                    })()}
                </div>
            `;
        }
        
        content += '</div>';
        return content;
    };

    // Delete submission function
    const deleteSubmission = async () => {
        try {
            await dbService.deleteSubmission(submission.id);
            // Call the onDelete callback to update the parent component's state
            if (onDelete) {
                onDelete(submission.id);
            } else {
                onBack(); // Fallback: Return to the results overview
            }
        } catch (error) {
            console.error('Error deleting submission:', error);
            alert('Fehler beim Löschen der Bewerbung');
        }
    };

    // Ensure analysis is loaded when component mounts or submission changes
    useEffect(() => {
        if (submission.analysis) {
            setAnalysis(submission.analysis);
        }
    }, [submission.analysis]);

    // Ensure qualification evaluation is loaded when component mounts or submission changes
    useEffect(() => {
        if (submission.qualification_evaluation) {
            setEligibilityScore(getQualificationScoreFromData(submission.qualification_evaluation, submission.point_evaluation));
            setQuestionEvaluations(submission.qualification_evaluation.questionEvaluations || []);
        }
    }, [submission.qualification_evaluation, submission.point_evaluation]);

    // Prevent automatic scrolling and ensure page stays at top
    useEffect(() => {
        // Scroll to top immediately when component mounts
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        
        // Prevent any delayed scrolling attempts
        const preventScroll = () => {
            window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        };
        
        // Set up multiple checks to prevent scrolling
        const timeouts = [
            setTimeout(preventScroll, 0),
            setTimeout(preventScroll, 10),
            setTimeout(preventScroll, 50),
            setTimeout(preventScroll, 100)
        ];
        
        return () => {
            timeouts.forEach(timeout => clearTimeout(timeout));
        };
    }, [submission.id]); // Re-run when submission changes

    // --- AI Evaluation pipeline (scoring -> qualification -> analysis over one Gemini chat) ---
    const runFullEvaluation = async () => {
        if (!questionnaire) {
            setError('Fragebogen konnte nicht geladen werden');
            return;
        }

        setIsLoading(true);
        setError(null);
        setAnalysis(null);
        setPointEvaluation(null);
        setEligibilityScore(null);
        setQuestionEvaluations([]);
        setNotification(null);
        setPipelineProgress(buildInitialProgress());

        // Mitschreiben, welches Modell gerade drankommt. Ohne das sieht ein
        // Durchlauf ueber die ganze Modellliste aus wie ein einzelner
        // Fehlschlag — der Fallback laeuft, man sieht ihn nur nicht.
        const unsubscribe = subscribeToAttempts((event) =>
            setPipelineProgress((prev) => markAttempt(prev, event))
        );

        try {
            const {
                analysis: newAnalysis,
                qualification_evaluation: newQualificationEvaluation,
                point_evaluation: newPointEvaluation
            } = await evaluationPipeline.run({
                questionnaire,
                submission,
                onStepStart: (stepId) => setPipelineProgress((prev) => markStepStart(prev, stepId)),
                onStepEnd: (stepId, durationMs) => setPipelineProgress((prev) => markStepEnd(prev, stepId, durationMs))
            });

            setAnalysis(newAnalysis);
            setEligibilityScore(newQualificationEvaluation.score);
            setQuestionEvaluations(newQualificationEvaluation.questionEvaluations);
            setPointEvaluation(newPointEvaluation);

            if (DB_CONFIG.type === 'pocketbase') {
                try {
                    await pb.collection('submissions').update(submission.id, {
                        analysis: newAnalysis,
                        qualification_evaluation: newQualificationEvaluation,
                        point_evaluation: newPointEvaluation
                    });
                    const updatedSubmission = {
                        ...submission,
                        analysis: newAnalysis,
                        qualification_evaluation: newQualificationEvaluation,
                        point_evaluation: newPointEvaluation
                    };
                    if (onUpdate) onUpdate(updatedSubmission);
                } catch (saveErr) {
                    console.error('Error saving evaluation to PocketBase:', saveErr);
                }
            }

            setNotification({ message: 'Bewertung erfolgreich abgeschlossen!', type: 'success' });
        } catch (err) {
            if (err.code === 'EMPTY') {
                setNotification({ message: err.message, type: 'error' });
            } else {
                console.error('Error during evaluation:', err);
                setError(err.message);
                setNotification({ message: `Fehler bei der Bewertung: ${err.message}`, type: 'error' });
            }
        } finally {
            unsubscribe();
            setIsLoading(false);
        }
    };

    const handlePointOverride = async (questionId, newScore) => {
        if (!pointEvaluation) return;
        const maxScorePerQ = pointEvaluation.questionScores.find(qs => qs.questionId === questionId)?.maxScore || 0;
        const clampedScore = Math.min(Math.max(0, parseInt(newScore) || 0), maxScorePerQ);
        const updatedScores = pointEvaluation.questionScores.map(qs => {
            if (qs.questionId !== questionId) return qs;
            return { ...qs, score: clampedScore, source: 'manual' };
        });
        const originalScore = pointEvaluation.questionScores.find(qs => qs.questionId === questionId)?.score || 0;
        const updatedOverrides = {
            ...(pointEvaluation.manualOverrides || {}),
            [questionId]: { original: originalScore, adjusted: clampedScore }
        };
        const base = updatedScores.reduce((sum, qs) => sum + (Number(qs.score) || 0), 0);
        const adj = Number(pointEvaluation.manualAdjustment) || 0;
        const max = Number(pointEvaluation.maxScore) || (base + adj);
        const newTotal = Math.min(Math.max(0, base + adj), max);
        const updated = { ...pointEvaluation, totalScore: newTotal, questionScores: updatedScores, manualOverrides: updatedOverrides };
        setPointEvaluation(updated);
        setEditingPointQuestionId(null);
        setEditingPointValue(null);
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                await pb.collection('submissions').update(submission.id, {
                    point_evaluation: updated
                });
                const updatedSubmission = { ...submission, point_evaluation: updated };
                if (onUpdate) onUpdate(updatedSubmission);
            } catch (err) {
                console.error('Error saving point override:', err);
            }
        }
    };

    // Bonus / Malus — a manual adjustment (positive or negative) added to the
    // base point sum to form the final total. Clamped to [0, maxScore].
    const handleAdjustmentSave = async (rawValue) => {
        if (!pointEvaluation) return;
        const adj = Math.round(Number(rawValue) || 0);
        const base = (pointEvaluation.questionScores || []).reduce((sum, qs) => sum + (Number(qs.score) || 0), 0);
        const max = Number(pointEvaluation.maxScore) || (base + adj);
        const newTotal = Math.min(Math.max(0, base + adj), max);
        const updated = { ...pointEvaluation, manualAdjustment: adj, totalScore: newTotal };
        setPointEvaluation(updated);
        setEditingAdjustment(false);
        setEditedAdjustment(null);
        if (DB_CONFIG.type === 'pocketbase') {
            try {
                await pb.collection('submissions').update(submission.id, { point_evaluation: updated });
                const updatedSubmission = { ...submission, point_evaluation: updated };
                if (onUpdate) onUpdate(updatedSubmission);
            } catch (err) {
                console.error('Error saving bonus/malus adjustment:', err);
            }
        }
    };

    // Status change from the detail view: persist through the list handler,
    // then reflect it locally so the open detail does not keep the old value.
    const handleStatusChange = async (submissionId, newStatus) => {
        await onStatusChange?.(submissionId, newStatus);
        onUpdate?.({ ...submission, status: newStatus });
    };

    // Notes feed functions
    const detailAuthor = () => {
        const u = pb.authStore.record || pb.authStore.model;
        return u ? { id: u.id, name: u.name || u.username || u.email || '' } : { id: '', name: '' };
    };
    const detailNoteId = () =>
        (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `n_${Date.now()}_${Math.random().toString(36).slice(2)}`;

    const persistDetailEntries = async (entries) => {
        const previous = noteEntries;
        setNoteEntries(entries);
        try {
            await dbService.updateSubmission(submission.id, { noteEntries: entries });
            const merged = { ...submission, noteEntries: entries, notes: entries[0]?.text || '', analysis: { ...(submission.analysis || {}), noteEntries: entries, notes: entries[0]?.text || '' } };
            if (onUpdate) onUpdate(merged);
            return true;
        } catch (error) {
            console.error('Error saving notes:', error);
            setNoteEntries(previous);
            setError('Fehler beim Speichern der Notiz');
            return false;
        }
    };

    const handleAddPost = async () => {
        if (!newPost.trim()) return;
        const entries = addNoteEntry(noteEntries, { id: detailNoteId(), text: newPost, author: detailAuthor(), createdAt: new Date().toISOString() });
        const ok = await persistDetailEntries(entries);
        if (ok) setNewPost('');
    };
    const handleEditPost = async () => {
        if (!editingId || !editingText.trim()) return;
        const entries = editNoteEntry(noteEntries, editingId, editingText, new Date().toISOString());
        const ok = await persistDetailEntries(entries);
        if (ok) { setEditingId(null); setEditingText(''); }
    };
    const handleDeletePost = async (id) => {
        await persistDetailEntries(deleteNoteEntry(noteEntries, id));
    };

    const handleReevaluate = () => {
        setShowReevaluateConfirm(true);
    };

    const confirmReevaluate = async () => {
        setShowReevaluateConfirm(false);
        await runFullEvaluation();
    };

    const cancelReevaluate = () => {
        setShowReevaluateConfirm(false);
    };

    // Helper function to get evaluation for a specific question
    const getQuestionEvaluation = (questionId) => {
        return questionEvaluations.find(evaluation => evaluation.questionId === questionId);
    };

    if (!questionnaire) {
        if (loadingQuestionnaire || isLoading) {
            return (
                <div className="min-h-screen bg-slate-100 dark:bg-slate-900 flex items-center justify-center">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
                        <p className="mt-4 text-slate-600 dark:text-slate-400">Lade Fragebogen...</p>
                    </div>
                </div>
            );
        }
        
        if (error) {
            return (
                <div className="min-h-screen bg-slate-100 dark:bg-slate-900 flex items-center justify-center">
                    <div className="text-center p-8">
                        <div className="font-bold text-red-600 mb-2">Fehler beim Laden</div>
                        <div className="text-sm text-slate-600 dark:text-slate-400 mb-4">{error}</div>
                        <button 
                            onClick={onBack}
                            className="px-4 py-2 bg-slate-600 text-white rounded hover:bg-slate-700 transition-colors"
                        >
                            Zurück zur Übersicht
                        </button>
                    </div>
                </div>
            );
        }
        
        return (
            <div className="min-h-screen bg-slate-100 dark:bg-slate-900 flex items-center justify-center">
                <div className="text-center p-8">
                    <div className="font-bold text-red-600 mb-2">Fragebogen nicht gefunden</div>
                    <div className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                        Der Fragebogen konnte nicht geladen werden.
                    </div>
                    <button 
                        onClick={onBack}
                        className="px-4 py-2 bg-slate-600 text-white rounded hover:bg-slate-700 transition-colors"
                    >
                        Zurück zur Übersicht
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-slate-100 dark:bg-slate-900 dark:text-slate-200 min-h-screen font-sans" style={{ scrollBehavior: 'auto' }}>
            {/* Notification */}
            {notification && (
                <Notification 
                    message={notification.message} 
                    type={notification.type} 
                    onClose={() => setNotification(null)} 
                />
            )}
            
            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-slate-800 dark:text-slate-200 p-6 rounded-2xl shadow-2xl max-w-md w-full m-4 transform transition-all duration-300">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="shrink-0">
                                <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                            </div>
                            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">Ergebnis löschen?</h2>
                        </div>
                        <p className="text-slate-600 dark:text-slate-300 mb-6">
                            Möchten Sie das Ergebnis von <strong>"{submission.applicantName}"</strong> wirklich löschen?
                            <br /><br />
                            Diese Aktion kann nicht rückgängig gemacht werden.
                        </p>
                        <div className="flex gap-3">
                            <button 
                                onClick={() => setShowDeleteModal(false)} 
                                className="flex-1 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                            >
                                Abbrechen
                            </button>
                            <button 
                                onClick={async () => {
                                    setShowDeleteModal(false);
                                    await deleteSubmission();
                                }} 
                                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                            >
                                Löschen
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <div className="container mx-auto px-4 pt-4 sm:px-6 sm:pt-6 lg:px-8 lg:pt-8 pb-24 max-w-7xl">
                <header className="mb-6 sm:mb-8">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-0 mb-4">
                        <button onClick={onBack} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-green-700 transition-colors">
                            <ArrowLeftIcon />Zurück zur Übersicht
                        </button>
                        <div className="flex gap-2 self-start">
                            <button 
                                onClick={printSubmission}
                                className="flex items-center gap-2 px-3 py-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                title="Ergebnis drucken"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                </svg>
                                Drucken
                            </button>
                            <button 
                                onClick={() => setShowDeleteModal(true)}
                                className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                title="Ergebnis löschen"
                            >
                                <TrashIcon />
                                Löschen
                            </button>
                        </div>
                    </div>
                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800 dark:text-slate-100 wrap-break-word">{submission.applicantName}</h1>
                    <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-slate-500 dark:text-slate-400">
                        <span>Erstellt von: <span className="font-medium text-slate-700 dark:text-slate-200">{userDisplayName(questionnaire?.createdBy)}</span></span>
                        {questionnaire?.created && (
                            <span>Erstellt am: <span className="font-medium text-slate-700 dark:text-slate-200">{new Date(questionnaire.created).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span></span>
                        )}
                        <label className="flex items-center gap-2">
                            <span>Zuständig:</span>
                            <select
                                value={submission.assignedTo?.id || questionnaire?.assignedTo?.id || ''}
                                onChange={async (e) => {
                                    const userId = e.target.value;
                                    const result = await onAssignSubmission?.(submission.id, userId);
                                    if (result?.success) {
                                        onUpdate?.({ ...submission, assignedTo: result.assignedTo });
                                    }
                                }}
                                className="px-2 py-1 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200"
                            >
                                <option value="">—</option>
                                {accounts.map((account) => (
                                    <option key={account.id} value={account.id}>{userDisplayName(account)}</option>
                                ))}
                            </select>
                        </label>
                    </div>
                </header>

                <PreviousApplications
                    submission={submission}
                    related={relatedApplications}
                    onOpen={onOpenSubmission}
                />

                <SubmissionIntegrityNotice submission={submission} recoveredCount={recoveredQuestions.length} />

                {/* Eligibility Card */}
                <EligibilityCard
                    assessmentTitle={questionnaire?.title || "Assessment Einzelhandelsfachkraft"}
                    templateName={questionnaire?.templateName}
                    submittedAt={submission.submittedAt}
                    duration={submission.completionDuration}
                    score={eligibilityScore}
                    maxScore={qualificationMaxScore}
                    analysisScore={analysis?.score ?? null}
                    analysisMaxScore={analysis?.score !== undefined && analysis?.score !== null ? 100 : null}
                    totalScore={totalPointTotals?.score ?? null}
                    totalMaxScore={totalPointTotals?.maxScore ?? null}
                    pageBreakdown={pointEvaluation ? buildPointPageGroups(pointEvaluation, orderedQuestionSections).groups : []}
                    bonusControl={pointEvaluation ? (() => {
                        const adjustment = Number(pointEvaluation.manualAdjustment) || 0;
                        return (
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div className="min-w-0">
                                    <h3 className="font-bold text-slate-800 dark:text-slate-100 whitespace-nowrap">Bonus / Malus</h3>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">Manuelle Anpassung des Gesamtscores</p>
                                </div>
                                {editingAdjustment ? (
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="number"
                                            value={editedAdjustment ?? adjustment}
                                            onChange={(e) => setEditedAdjustment(e.target.value)}
                                            className="w-20 px-3 py-2 text-center text-lg font-bold border border-amber-300 dark:border-amber-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
                                            autoFocus
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') handleAdjustmentSave(editedAdjustment);
                                                if (e.key === 'Escape') { setEditingAdjustment(false); setEditedAdjustment(null); }
                                            }}
                                        />
                                        <button onClick={() => handleAdjustmentSave(editedAdjustment)} className="px-3 py-2 text-sm font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700 whitespace-nowrap">Speichern</button>
                                        <button onClick={() => { setEditingAdjustment(false); setEditedAdjustment(null); }} className="px-3 py-2 text-sm font-semibold bg-slate-500 text-white rounded-lg hover:bg-slate-600 whitespace-nowrap">Abbrechen</button>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-3">
                                        <span className={`text-2xl font-bold ${adjustment > 0 ? 'text-green-700 dark:text-green-400' : adjustment < 0 ? 'text-red-700 dark:text-red-400' : 'text-slate-400 dark:text-slate-500'}`}>
                                            {adjustment > 0 ? '+' : ''}{adjustment}
                                        </span>
                                        <button onClick={() => { setEditingAdjustment(true); setEditedAdjustment(adjustment); }} className="inline-flex items-center gap-2 px-3 py-1 text-sm font-medium bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 whitespace-nowrap" title="Bonus/Malus anpassen">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                            Anpassen
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })() : null}
                    status={submission.status}
                />
                

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 lg:gap-8">
                    <div id="eingereichte-antworten" className="bg-white dark:bg-slate-800 dark:text-slate-200 p-4 sm:p-6 lg:p-8 rounded-2xl shadow-lg space-y-4 sm:space-y-6">
                        <h2 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-slate-100">Eingereichte Antworten</h2>

                        {/* Welcher Fragebogen — eine Ebene über dessen Seiten.
                            Bewusst eine Pillen-Gruppe und keine zweite Reihe
                            Unterstrich-Reiter: zwei gleich aussehende Reihen
                            übereinander lesen sich als eine. Dasselbe Element,
                            mit dem der Fragebogen-2-Dialog zwischen seinen zwei
                            Seiten umschaltet. */}
                        <div className="flex w-fit gap-1 rounded-xl bg-slate-100 p-1 dark:bg-slate-900/60">
                            {[
                                { id: FIRST_TAB, label: 'Fragebogen 1' },
                                {
                                    id: ASSESSMENT_TAB,
                                    label: 'Fragebogen 2',
                                    // Solange der Stand nicht feststeht, lieber kein
                                    // Punkt als ein falscher: "weiß ich nicht" darf
                                    // nicht als "nicht verschickt" ankommen.
                                    dot: loadingInvites || inviteError ? null : assessmentDot(invites[0]),
                                },
                            ].map((tab) => (
                                <button
                                    key={tab.id}
                                    type="button"
                                    onClick={() => setQuestionnaireTab(tab.id)}
                                    title={tab.dot?.title}
                                    className={`inline-flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors duration-200 ${
                                        questionnaireTab === tab.id
                                            ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                                            : 'text-slate-600 hover:bg-slate-200/70 dark:text-slate-300 dark:hover:bg-slate-700/60'
                                    }`}
                                >
                                    {tab.label}
                                    {tab.dot && (
                                        <>
                                            <span className={`h-2 w-2 shrink-0 rounded-full ${tab.dot.className}`} aria-hidden="true" />
                                            <span className="sr-only">{tab.dot.title}</span>
                                        </>
                                    )}
                                </button>
                            ))}
                        </div>

                        {questionnaireTab === ASSESSMENT_TAB && (
                            <AssessmentPanel
                                invites={invites}
                                loading={loadingInvites}
                                error={inviteError}
                                onSend={() => setShowSendAssessment(true)}
                            />
                        )}

                        {/* Tabs */}
                        {questionnaireTab === FIRST_TAB && (
                        <div className="flex flex-wrap gap-1 border-b border-slate-200 dark:border-slate-700 pb-1">
                            {answerTabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`px-3 py-2 font-medium text-sm sm:text-base transition-colors duration-200 text-left whitespace-normal wrap-break-word leading-tight ${
                                        activeTab === tab.id
                                            ? 'text-green-600 dark:text-green-400 border-b-2 border-green-600 dark:border-green-400'
                                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                                    }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                        )}

                        {/* Tab Content */}
                        {activeSection?.type === 'default' && (
                            <div className="space-y-4">
                                {/* Name field */}
                                <div className="space-y-2">
                                    <p className="font-semibold text-sm sm:text-base text-slate-700 dark:text-slate-300">Name</p>
                                    <div className="mt-2 p-3 bg-slate-200 dark:bg-slate-700 rounded-lg border border-slate-300 dark:border-slate-600 text-sm sm:text-base text-slate-800 dark:text-slate-200 wrap-break-word">
                                        {submission.applicantName || <span className="text-slate-400 dark:text-slate-500">Keine Antwort</span>}
                                    </div>
                                    {submission.applicantName && <RedactionMarker label="Name" />}
                                </div>
                                
                                {(() => { const defaultLabelMap = computeDisplayLabels(defaultQuestions, 0); return defaultQuestions.filter(q => q.id !== 'system_name').filter(q => wasQuestionVisible(q, submission.answers)).map((q, index) => {
                                    const evaluation = getQuestionEvaluation(q.id);
                                    const pointScore = pointEvaluation?.questionScores?.find(qs => String(qs.questionId) === String(q.id));
                                    return (
                                        <div key={q.id} data-answer-question-id={q.id} className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <p className="font-semibold text-sm sm:text-base text-slate-700 dark:text-slate-300" dangerouslySetInnerHTML={{ __html: `${getQuestionNumber(q, defaultLabelMap)}. ${sanitizeRichText(q.text)}` }} />
                                                <div className="flex items-center gap-2">
                                                <QuestionObligationBadge question={q} />
                                                {pointScore?.redFlag && (
                                                    <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" title={pointScore.reason || 'Als Red Flag eingestuft'}>
                                                        🚩 Red Flag
                                                    </span>
                                                )}
                                                {pointScore && (
                                                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${getPointScoreColor(pointScore.score, pointScore.maxScore)} ${getPointScoreTextColor(pointScore.score, pointScore.maxScore)}`}>
                                                        {pointScore.score}/{pointScore.maxScore}
                                                    </span>
                                                )}
                                                {evaluation && (
                                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                                        evaluation.passed
                                                            ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                                                            : 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                                                    }`}>
                                                        {evaluation.passed ? (
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                            </svg>
                                                        ) : (
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                )}
                                                </div>
                                            </div>
                                            {q.description && <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400" dangerouslySetInnerHTML={{ __html: sanitizeRichText(q.description) }} />}
                                            {q.adminNote && (
                                                <div className="text-xs p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300">
                                                    <span className="font-medium">Interne Notiz:</span> {q.adminNote}
                                                </div>
                                            )}

                                            <div className="mt-2 p-3 bg-slate-200 dark:bg-slate-700 rounded-lg border border-slate-300 dark:border-slate-600 text-sm sm:text-base text-slate-800 dark:text-slate-200 wrap-break-word">
                                                {q.type === 'multiple-choice' && isQuestionAnswered(q.id, q.type, submission.answers, q.answerType, q.options) ? (
                                                    <div className="space-y-2">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <div className="flex items-center gap-1">
                                                                <div className="w-4 h-4 border-2 border-blue-600 rounded flex items-center justify-center">
                                                                    <span className="text-blue-600 font-bold text-xs">✓</span>
                                                                </div>
                                                                <div className="w-4 h-4 border-2 border-blue-600 rounded flex items-center justify-center">
                                                                    <span className="text-blue-600 font-bold text-xs">✓</span>
                                                                </div>
                                                                <div className="w-4 h-4 border-2 border-blue-600 rounded flex items-center justify-center">
                                                                    <span className="text-blue-600 font-bold text-xs">✓</span>
                                                                </div>
                                                                <span className="text-blue-600 font-medium text-sm">...</span>
                                                            </div>
                                                            <span className="text-sm font-medium text-blue-700 dark:text-blue-400">
                                                                Mehrfachauswahl - {submission.answers[q.id]?.length || 0} von {q.maxSelections || q.options?.length || 0} Optionen gewählt
                                                                {q.maxSelections && (
                                                                    <span className="block text-xs text-blue-600 dark:text-blue-400 mt-1">
                                                                        (Limit: {q.maxSelections} auswählbar)
                                                                    </span>
                                                                )}
                                                            </span>
                                                        </div>
                                                        <div className="space-y-2">
                                                            {q.options?.map((option, index) => {
                                                                const isSelected = (submission.answers[q.id] || []).includes(option);
                                                                const isCorrect = (q.correctOptions || []).includes(index);
                                                                return (
                                                                    <div key={index} className={`flex items-center gap-3 p-2 rounded-lg border ${
                                                                        isSelected
                                                                            ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600'
                                                                            : isCorrect
                                                                                ? 'bg-white dark:bg-slate-600 border-green-300 dark:border-green-600'
                                                                                : 'bg-white dark:bg-slate-600 border-slate-300 dark:border-slate-500'
                                                                    }`}>
                                                                        <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                                                                            isSelected
                                                                                ? 'bg-blue-600 text-white'
                                                                                : 'border-2 border-slate-300 dark:border-slate-500'
                                                                        }`}>
                                                                            {isSelected && <span className="font-bold text-sm">✓</span>}
                                                                        </div>
                                                                        <span className={`font-medium ${
                                                                            isSelected
                                                                                ? 'text-blue-700 dark:text-blue-300'
                                                                                : 'text-slate-500 dark:text-slate-400'
                                                                        }`}>
                                                                            {option}
                                                                        </span>
                                                                        {isCorrect && (
                                                                            <span className="ml-auto text-xs px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-medium">Korrekt</span>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                ) : q.type === 'single-choice' && isQuestionAnswered(q.id, q.type, submission.answers, q.answerType, q.options) ? (
                                                    <div className="space-y-2">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <div className="flex items-center gap-1">
                                                                <div className="w-4 h-4 border-2 border-blue-600 rounded-full flex items-center justify-center">
                                                                    <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                                                                </div>
                                                                <span className="text-blue-600 font-medium text-sm">Einzelauswahl - 1 Option gewählt</span>
                                                            </div>
                                                        </div>
                                                        <div className="space-y-2">
                                                            {q.options?.map((option, index) => {
                                                                const isSelected = submission.answers[q.id] === option;
                                                                const isCorrect = (q.correctOptions || []).includes(index);
                                                                return (
                                                                    <div key={index} className={`flex items-center gap-3 p-2 rounded-lg border ${
                                                                        isSelected
                                                                            ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600'
                                                                            : isCorrect
                                                                                ? 'bg-white dark:bg-slate-600 border-green-300 dark:border-green-600'
                                                                                : 'bg-white dark:bg-slate-600 border-slate-300 dark:border-slate-500'
                                                                    }`}>
                                                                        <div className={`flex items-center justify-center w-6 h-6 rounded-full border-2 ${
                                                                            isSelected
                                                                                ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                                                                                : 'border-slate-300 dark:border-slate-500'
                                                                        }`}>
                                                                            {isSelected && <div className="w-3 h-3 bg-blue-600 rounded-full"></div>}
                                                                        </div>
                                                                        <span className={`font-medium ${
                                                                            isSelected
                                                                                ? 'text-blue-700 dark:text-blue-300'
                                                                                : 'text-slate-500 dark:text-slate-400'
                                                                        }`}>
                                                                            {option}
                                                                        </span>
                                                                        {isCorrect && (
                                                                            <span className="ml-auto text-xs px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-medium">Korrekt</span>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                ) : q.type === 'ranking' && isQuestionAnswered(q.id, q.type, submission.answers, q.answerType, q.options) ? (
                                                    <div className="space-y-2">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <div className="flex items-center gap-1">
                                                                <div className="w-4 h-4 border-2 border-blue-600 rounded flex items-center justify-center">
                                                                    <span className="text-blue-600 font-bold text-xs">1</span>
                                                                </div>
                                                                <div className="w-4 h-4 border-2 border-blue-600 rounded flex items-center justify-center">
                                                                    <span className="text-blue-600 font-bold text-xs">2</span>
                                                                </div>
                                                                <div className="w-4 h-4 border-2 border-blue-600 rounded flex items-center justify-center">
                                                                    <span className="text-blue-600 font-bold text-xs">3</span>
                                                                </div>
                                                                <span className="text-blue-600 font-medium text-sm">...</span>
                                                            </div>
                                                            <span className="text-sm font-medium text-blue-700 dark:text-blue-400">Rangordnung - {submission.answers[q.id]?.length || 0} von {q.options?.length || 0} gewählt (1 = Am wichtigsten)</span>
                                                        </div>
                                                        <div className="space-y-2">
                                                            {getOrderedRankingEntries(submission.answers[q.id] || [], q.options || []).map((entry, index) => {
                                                                return (
                                                                    <div key={index} className="flex items-center gap-3 p-2 rounded-lg border bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600">
                                                                        <div className="flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm bg-blue-600 text-white">
                                                                            {entry.rank}
                                                                        </div>
                                                                        <span className="font-medium text-blue-700 dark:text-blue-300">
                                                                            {entry.label}
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                ) : (q.type === 'matrix' || q.type === 'combobox') && isQuestionAnswered(q.id, q.type, submission.answers, q.answerType, q.options) ? (
                                                    <div className="space-y-2">
                                                        <div className="flex items-center gap-1 mb-2">
                                                            <div className="w-4 h-4 border-2 border-blue-600 rounded flex items-center justify-center">
                                                                <svg className="w-2.5 h-2.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                                                </svg>
                                                            </div>
                                                            <span className="text-blue-600 font-medium text-sm">{q.type === 'combobox' ? 'Combobox' : 'Matrix'} - {(submission.answers[q.id] || []).filter(r => r && r.value).length} {(submission.answers[q.id] || []).filter(r => r && r.value).length === 1 ? 'Eintrag' : 'Einträge'}</span>
                                                        </div>
                                                        <div className="space-y-2">
                                                            {(submission.answers[q.id] || []).filter(row => row && row.value).map((row, index) => (
                                                                <div key={index} className="flex items-center gap-3 p-2 rounded-lg border bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600">
                                                                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white">
                                                                        <span className="font-bold text-xs">{index + 1}</span>
                                                                    </div>
                                                                    <span className="font-medium text-blue-700 dark:text-blue-300">{row.label}</span>
                                                                    <svg className="w-4 h-4 text-slate-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                                    </svg>
                                                                    <span className="font-medium text-blue-700 dark:text-blue-300">{row.value}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ) : q.type === 'file-upload' && isQuestionAnswered(q.id, q.type, submission.answers) ? (
                                                    <div className="space-y-2">
                                                        <div className="flex items-center gap-1 mb-2">
                                                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                            </svg>
                                                            <span className="text-blue-600 font-medium text-sm">Datei-Upload - {submission.answers[q.id]?.fileIds?.length || 0} Datei(en)</span>
                                                        </div>
                                                        {(submission.answers[q.id]?.fileNames || []).map((name, idx) => {
                                                            const fileId = submission.answers[q.id]?.fileIds?.[idx];
                                                            const downloadUrl = fileId && submissionFileUrls[submission.id]?.[fileId];
                                                            return (
                                                            <div key={idx} className="flex items-center gap-2 p-2 rounded-lg border bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600">
                                                                <svg className="w-4 h-4 text-blue-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                                </svg>
                                                                {downloadUrl ? (
                                                                    <a href={downloadUrl} target="_blank" rel="noopener noreferrer" className="font-medium text-blue-700 dark:text-blue-300 text-sm truncate hover:underline">{name}</a>
                                                                ) : (
                                                                    <span className="font-medium text-blue-700 dark:text-blue-300 text-sm truncate">{name}</span>
                                                                )}
                                                            </div>
                                                            );
                                                        })}
                                                        {submission.answers[q.id]?.note && (
                                                            <div className="text-sm text-slate-600 dark:text-slate-300 mt-1 italic">Notiz: {submission.answers[q.id].note}</div>
                                                        )}
                                                        <FileUploadAiHint />
                                                    </div>
                                                ) : isQuestionAnswered(q.id, q.type, submission.answers, q.answerType, q.options) ?
                                                    (Array.isArray(submission.answers[q.id]) ? submission.answers[q.id].join(', ') : submission.answers[q.id]) :
                                                    <span className="text-slate-400 dark:text-slate-500">{getUnansweredText(q.type)}</span>
                                                }
                                            </div>

                                            <RedactionHint answer={submission.answers[q.id]} />

                                            {q.id === firstEmailQuestionId && (
                                                <button
                                                    type="button"
                                                    onClick={() => setShowSendAssessment(true)}
                                                    className="mt-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
                                                >
                                                    Fragebogen 2 senden →
                                                </button>
                                            )}

                                            {pointScore && pointScore.reason && (
                                                <div className="text-xs p-2 rounded-lg bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
                                                    {pointScore.reason}
                                                </div>
                                            )}
                                        </div>
                                    );
                                }); })()}
                            </div>
                        )}

                        {activeSection?.recovered && (
                            <div className="mb-4 p-3 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20">
                                <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                                    Diese Antworten gehören zu Fragen, die der Fragebogen nicht mehr enthält.
                                </p>
                                <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                                    Sie sind vollständig gespeichert — nur die Frage dazu wurde seit der Bewerbung
                                    geändert, umsortiert oder neu importiert. Der Fragetext stammt aus der Bewerbung
                                    selbst, nicht aus dem heutigen Fragebogen.
                                </p>
                            </div>
                        )}

                        {activeSection?.type === 'custom' && activeSection.questions.length > 0 && (
                            <div className="space-y-4">
                                {(() => { const customLabelMap = computeDisplayLabels(activeSection.questions); return activeSection.questions.filter(q => wasQuestionVisible(q, submission.answers)).map(q => {
                                    const pointScore = pointEvaluation?.questionScores?.find(qs => String(qs.questionId) === String(q.id));
                                    return (
                                    <div key={q.id} data-answer-question-id={q.id} className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <p className="font-semibold text-sm sm:text-base text-slate-700 dark:text-slate-300" dangerouslySetInnerHTML={{ __html: `${getQuestionNumber(q, customLabelMap)}. ${sanitizeRichText(q.text)}` }} />
                                            <div className="flex items-center gap-2">
                                            <QuestionObligationBadge question={q} />
                                            {pointScore?.redFlag && (
                                                <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" title={pointScore.reason || 'Als Red Flag eingestuft'}>
                                                    🚩 Red Flag
                                                </span>
                                            )}
                                            {pointScore && (
                                                <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${getPointScoreColor(pointScore.score, pointScore.maxScore)} ${getPointScoreTextColor(pointScore.score, pointScore.maxScore)}`}>
                                                    {pointScore.score}/{pointScore.maxScore}
                                                </span>
                                            )}
                                            </div>
                                        </div>
                                        {q.description && <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400" dangerouslySetInnerHTML={{ __html: sanitizeRichText(q.description) }} />}
                                        {q.adminNote && (
                                            <div className="text-xs p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300">
                                                <span className="font-medium">Interne Notiz:</span> {q.adminNote}
                                            </div>
                                        )}

                                        {/* Display image for image questions */}
                                        {q.type === 'image' && (q.imageUrl || q.imageDataUrl) && (
                                            <div className="mt-2 mb-2">
                                                <img src={q.imageUrl || q.imageDataUrl} alt="Fragebild" className="max-h-32 sm:max-h-48 rounded-lg border border-slate-200 dark:border-slate-700 object-contain w-full" />
                                            </div>
                                        )}
                                        
                                        <div className="mt-2 p-3 bg-slate-200 dark:bg-slate-700 rounded-lg border border-slate-300 dark:border-slate-600 text-sm sm:text-base text-slate-800 dark:text-slate-200 wrap-break-word">
                                            {q.type === 'ranking' && isQuestionAnswered(q.id, q.type, submission.answers, q.answerType, q.options) ? (
                                                <div className="space-y-2">
                                                    <div className="flex items-center gap-2 mb-2">
                                                    <div className="flex items-center gap-1">
                                                        <div className="w-4 h-4 border-2 border-blue-600 rounded flex items-center justify-center">
                                                            <span className="text-blue-600 font-bold text-xs">1</span>
                                                        </div>
                                                        <div className="w-4 h-4 border-2 border-blue-600 rounded flex items-center justify-center">
                                                            <span className="text-blue-600 font-bold text-xs">2</span>
                                                        </div>
                                                        <div className="w-4 h-4 border-2 border-blue-600 rounded flex items-center justify-center">
                                                            <span className="text-blue-600 font-bold text-xs">3</span>
                                                        </div>
                                                        <span className="text-blue-600 font-medium text-sm">...</span>
                                                    </div>
                                                    <span className="text-sm font-medium text-blue-700 dark:text-blue-400">Rangordnung - {submission.answers[q.id]?.length || 0} von {q.options?.length || 0} gewählt (1 = Am wichtigsten)</span>
                                                    </div>
                                                    <div className="space-y-2">
                                                        {getOrderedRankingEntries(submission.answers[q.id] || [], q.options || []).map((entry, index) => {
                                                            return (
                                                                <div key={index} className="flex items-center gap-3 p-2 rounded-lg border bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600">
                                                                    <div className="flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm bg-blue-600 text-white">
                                                                        {entry.rank}
                                                                    </div>
                                                                    <span className="font-medium text-blue-700 dark:text-blue-300">
                                                                        {entry.label}
                                                                    </span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            ) : q.type === 'multiple-choice' && isQuestionAnswered(q.id, q.type, submission.answers, q.answerType, q.options) ? (
                                                <div className="space-y-2">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <div className="flex items-center gap-1">
                                                            <div className="w-4 h-4 border-2 border-blue-600 rounded flex items-center justify-center">
                                                                <span className="text-blue-600 font-bold text-xs">✓</span>
                                                            </div>
                                                            <div className="w-4 h-4 border-2 border-blue-600 rounded flex items-center justify-center">
                                                                <span className="text-blue-600 font-bold text-xs">✓</span>
                                                            </div>
                                                            <div className="w-4 h-4 border-2 border-blue-600 rounded flex items-center justify-center">
                                                                <span className="text-blue-600 font-bold text-xs">✓</span>
                                                            </div>
                                                            <span className="text-blue-600 font-medium text-sm">...</span>
                                                        </div>
                                                        <span className="text-sm font-medium text-blue-700 dark:text-blue-400">
                                                            Mehrfachauswahl - {submission.answers[q.id]?.length || 0} von {q.maxSelections || q.options?.length || 0} Optionen gewählt
                                                            {q.maxSelections && (
                                                                <span className="block text-xs text-blue-600 dark:text-blue-400 mt-1">
                                                                    (Limit: {q.maxSelections} auswählbar)
                                                                </span>
                                                            )}
                                                        </span>
                                                    </div>
                                                    <div className="space-y-2">
                                                        {q.options?.map((option, index) => {
                                                            const isSelected = (submission.answers[q.id] || []).includes(option);
                                                            const isCorrect = (q.correctOptions || []).includes(index);
                                                            return (
                                                                <div key={index} className={`flex items-center gap-3 p-2 rounded-lg border ${
                                                                    isSelected
                                                                        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600'
                                                                        : isCorrect
                                                                            ? 'bg-white dark:bg-slate-600 border-green-300 dark:border-green-600'
                                                                            : 'bg-white dark:bg-slate-600 border-slate-300 dark:border-slate-500'
                                                                }`}>
                                                                    <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                                                                        isSelected
                                                                            ? 'bg-blue-600 text-white'
                                                                            : 'border-2 border-slate-300 dark:border-slate-500'
                                                                    }`}>
                                                                        {isSelected && <span className="font-bold text-sm">✓</span>}
                                                                    </div>
                                                                    <span className={`font-medium ${
                                                                        isSelected
                                                                            ? 'text-blue-700 dark:text-blue-300'
                                                                            : 'text-slate-500 dark:text-slate-400'
                                                                    }`}>
                                                                        {option}
                                                                    </span>
                                                                    {isCorrect && (
                                                                        <span className="ml-auto text-xs px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-medium">Korrekt</span>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            ) : q.type === 'single-choice' && isQuestionAnswered(q.id, q.type, submission.answers, q.answerType, q.options) ? (
                                                <div className="space-y-2">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <div className="flex items-center gap-1">
                                                            <div className="w-4 h-4 border-2 border-blue-600 rounded-full flex items-center justify-center">
                                                                <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                                                            </div>
                                                            <span className="text-blue-600 font-medium text-sm">Einzelauswahl - 1 Option gewählt</span>
                                                        </div>
                                                    </div>
                                                    <div className="space-y-2">
                                                        {q.options?.map((option, index) => {
                                                            const isSelected = submission.answers[q.id] === option;
                                                            const isCorrect = (q.correctOptions || []).includes(index);
                                                            return (
                                                                <div key={index} className={`flex items-center gap-3 p-2 rounded-lg border ${
                                                                    isSelected
                                                                        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600'
                                                                        : isCorrect
                                                                            ? 'bg-white dark:bg-slate-600 border-green-300 dark:border-green-600'
                                                                            : 'bg-white dark:bg-slate-600 border-slate-300 dark:border-slate-500'
                                                                }`}>
                                                                    <div className={`flex items-center justify-center w-6 h-6 rounded-full border-2 ${
                                                                        isSelected
                                                                            ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                                                                            : 'border-slate-300 dark:border-slate-500'
                                                                    }`}>
                                                                        {isSelected && <div className="w-3 h-3 bg-blue-600 rounded-full"></div>}
                                                                    </div>
                                                                    <span className={`font-medium ${
                                                                        isSelected
                                                                            ? 'text-blue-700 dark:text-blue-300'
                                                                            : 'text-slate-500 dark:text-slate-400'
                                                                    }`}>
                                                                        {option}
                                                                    </span>
                                                                    {isCorrect && (
                                                                        <span className="ml-auto text-xs px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-medium">Korrekt</span>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            ) : (q.type === 'matrix' || q.type === 'combobox') && isQuestionAnswered(q.id, q.type, submission.answers, q.answerType, q.options) ? (
                                                <div className="space-y-2">
                                                    <div className="flex items-center gap-1 mb-2">
                                                        <div className="w-4 h-4 border-2 border-blue-600 rounded flex items-center justify-center">
                                                            <svg className="w-2.5 h-2.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                                            </svg>
                                                        </div>
                                                        <span className="text-blue-600 font-medium text-sm">{q.type === 'combobox' ? 'Combobox' : 'Matrix'} - {(submission.answers[q.id] || []).filter(r => r && r.value).length} {(submission.answers[q.id] || []).filter(r => r && r.value).length === 1 ? 'Eintrag' : 'Einträge'}</span>
                                                    </div>
                                                    <div className="space-y-2">
                                                        {(submission.answers[q.id] || []).filter(row => row && row.value).map((row, index) => (
                                                            <div key={index} className="flex items-center gap-3 p-2 rounded-lg border bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600">
                                                                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white">
                                                                    <span className="font-bold text-xs">{index + 1}</span>
                                                                </div>
                                                                <span className="font-medium text-blue-700 dark:text-blue-300">{row.label}</span>
                                                                <svg className="w-4 h-4 text-slate-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                                </svg>
                                                                <span className="font-medium text-blue-700 dark:text-blue-300">{row.value}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ) : q.type === 'file-upload' && isQuestionAnswered(q.id, q.type, submission.answers) ? (
                                                <div className="space-y-2">
                                                    <div className="flex items-center gap-1 mb-2">
                                                        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                        </svg>
                                                        <span className="text-blue-600 font-medium text-sm">Datei-Upload - {submission.answers[q.id]?.fileIds?.length || 0} Datei(en)</span>
                                                    </div>
                                                    {(submission.answers[q.id]?.fileNames || []).map((name, idx) => {
                                                        const fileId = submission.answers[q.id]?.fileIds?.[idx];
                                                        const downloadUrl = fileId && submissionFileUrls[submission.id]?.[fileId];
                                                        return (
                                                            <div key={idx} className="flex items-center gap-2 p-2 rounded-lg border bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600">
                                                                <svg className="w-4 h-4 text-blue-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                                </svg>
                                                                {downloadUrl ? (
                                                                    <a href={downloadUrl} target="_blank" rel="noopener noreferrer" className="font-medium text-blue-700 dark:text-blue-300 text-sm truncate hover:underline">{name}</a>
                                                                ) : (
                                                                    <span className="font-medium text-blue-700 dark:text-blue-300 text-sm truncate">{name}</span>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                    {submission.answers[q.id]?.note && (
                                                        <div className="text-sm text-slate-600 dark:text-slate-300 mt-1 italic">Notiz: {submission.answers[q.id].note}</div>
                                                    )}
                                                    <FileUploadAiHint />
                                                </div>
                                            ) : isQuestionAnswered(q.id, q.type, submission.answers, q.answerType, q.options) ?
                                                (Array.isArray(submission.answers[q.id]) ? submission.answers[q.id].join(', ') : submission.answers[q.id]) :
                                                <span className="text-slate-400 dark:text-slate-500">{getUnansweredText(q.type)}</span>
                                            }
                                        </div>

                                        <RedactionHint answer={submission.answers[q.id]} />

                                        {q.id === firstEmailQuestionId && (
                                            <button
                                                type="button"
                                                onClick={() => setShowSendAssessment(true)}
                                                className="mt-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
                                            >
                                                Fragebogen 2 senden →
                                            </button>
                                        )}

                                        {pointScore && pointScore.reason && (
                                            <div className="text-xs p-2 rounded-lg bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
                                                {pointScore.reason}
                                            </div>
                                        )}
                                    </div>
                                    );
                                }); })()}
                            </div>
                        )}
                    </div>
                    <div className="bg-white dark:bg-slate-800 dark:text-slate-200 p-4 sm:p-6 lg:p-8 rounded-2xl shadow-lg">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-4 gap-3 sm:gap-0">
                            <div role="tablist" className="flex flex-wrap gap-1 border-b border-slate-200 dark:border-slate-700 pb-1">
                                <button
                                    role="tab"
                                    onClick={() => setAnalysisCardTab('analysis')}
                                    className={`px-3 py-2 font-medium text-sm sm:text-base transition-colors duration-200 text-left whitespace-normal wrap-break-word leading-tight ${
                                        analysisCardTab === 'analysis'
                                            ? 'text-green-600 dark:text-green-400 border-b-2 border-green-600 dark:border-green-400'
                                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                                    }`}
                                >
                                    Analyse
                                </button>
                                <button
                                    role="tab"
                                    onClick={() => setAnalysisCardTab('overview')}
                                    className={`px-3 py-2 font-medium text-sm sm:text-base transition-colors duration-200 text-left whitespace-normal wrap-break-word leading-tight ${
                                        analysisCardTab === 'overview'
                                            ? 'text-green-600 dark:text-green-400 border-b-2 border-green-600 dark:border-green-400'
                                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                                    }`}
                                >
                                    Übersicht
                                </button>
                            </div>
                            <div className="flex items-center gap-2 self-start sm:self-auto">
                            {analysis ? (
                                <button
                                    onClick={handleReevaluate}
                                    disabled={isLoading}
                                    className="px-3 py-1 text-sm bg-yellow-100 text-yellow-800 font-semibold rounded-lg hover:bg-yellow-200 disabled:bg-yellow-50 disabled:text-yellow-400 disabled:cursor-not-allowed flex items-center gap-1 self-start sm:self-auto"
                                >
                                    {isLoading ? (
                                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                    ) : (
                                        <RefreshIcon className="h-3 w-3" />
                                    )}
                                    {isLoading ? 'Bewertung läuft...' : 'Neu bewerten'}
                                </button>
                            ) : (
                                <button
                                    onClick={runFullEvaluation}
                                    disabled={isLoading}
                                    className="flex items-center gap-2 px-4 py-2 bg-green-800 text-white font-semibold rounded-lg hover:bg-green-900 disabled:bg-green-300 disabled:cursor-not-allowed"
                                >
                                    {isLoading ? (
                                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                    ) : (
                                        <SparklesIcon/>
                                    )}
                                    {isLoading ? 'Bewertung läuft...' : 'Bewertung starten'}
                                </button>
                            )}
                            </div>
                        </div>
                        
                        {error && <p className="text-red-600 text-sm mt-4">{error}</p>}

                        {isLoading && analysisCardTab === 'analysis' && (
                            <EvaluationProgress progress={pipelineProgress} />
                        )}

                        {analysisCardTab === 'analysis' && !isLoading && analysis && (
                            <div className="mt-6 space-y-4 animate-fade-in">
                                {(() => {
                                    // Live verdict from the current Gesamtscore — re-renders on
                                    // manual point overrides and Bonus/Malus changes.
                                    const recMax = Number(pointEvaluation?.maxScore) || 0;
                                    const recPct = recMax > 0 ? Math.round(((Number(pointEvaluation?.totalScore) || 0) / recMax) * 100) : 0;
                                    const rec = getRecommendation(recPct, recommendationTiers);
                                    return (
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Empfehlung:</span>
                                            <span
                                                className="inline-flex items-center rounded-full text-sm font-bold text-white"
                                                style={{ backgroundColor: rec.color, padding: '4px 12px', gap: '6px' }}
                                            >
                                                <span aria-hidden="true">{rec.emoji}</span>
                                                {rec.label}
                                            </span>
                                        </div>
                                    );
                                })()}
                                <div className="space-y-4">
                                    <div>
                                        <h3 className="font-bold text-slate-800 dark:text-slate-100">Zusammenfassung</h3>
                                        <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300 leading-relaxed">{analysis.summary || 'Keine Zusammenfassung verfügbar'}</p>
                                    </div>
                                </div>
                                {analysis.strengths && analysis.strengths.length > 0 && (
                                    <div className="space-y-2">
                                        <h3 className="font-bold text-slate-800 dark:text-slate-100">Erkannte Stärken</h3>
                                        <ul className="list-disc list-inside text-sm sm:text-base text-slate-600 dark:text-slate-300 space-y-1">
                                            {analysis.strengths.map((s, i) => <li key={i} className="leading-relaxed">{s}</li>)}
                                        </ul>
                                    </div>
                                )}
                                {analysis.weaknesses && analysis.weaknesses.length > 0 && (
                                    <div className="space-y-2">
                                        <h3 className="font-bold text-slate-800 dark:text-slate-100">Erkannte Schwächen</h3>
                                        <ul className="list-disc list-inside text-sm sm:text-base text-slate-600 dark:text-slate-300 space-y-1">
                                            {analysis.weaknesses.map((w, i) => <li key={i} className="leading-relaxed">{w}</li>)}
                                        </ul>
                                    </div>
                                )}
                                {analysis.areasForDiscussion && analysis.areasForDiscussion.length > 0 && (
                                    <div className="space-y-2">
                                        <h3 className="font-bold text-slate-800 dark:text-slate-100">Themen für das Interview</h3>
                                        <ul className="list-disc list-inside text-sm sm:text-base text-slate-600 dark:text-slate-300 space-y-1">
                                            {analysis.areasForDiscussion.map((d, i) => <li key={i} className="leading-relaxed">{d}</li>)}
                                    </ul>
                                    </div>
                                )}
                            </div>
                        )}

                        {analysisCardTab === 'analysis' && !analysis && (
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                Noch keine Analyse durchgeführt. Klicken Sie auf "Bewertung starten", um die Bewertung zu starten.
                            </p>
                        )}

                        {analysisCardTab === 'overview' && (
                            <div className="space-y-4">
                                {pointEvaluation ? (
                                    (() => {
                                        const { groups, questionById } = buildPointPageGroups(pointEvaluation, orderedQuestionSections);
                                        return (
                                        <div className="space-y-4 max-h-96 overflow-y-auto">
                                            {groups.map((page) => (
                                                <div key={page.key} className="space-y-1">
                                                    {/* Page header with its subtotal — page subtotals sum to the Gesamtscore */}
                                                    <div className="flex items-center justify-between gap-2 px-2 py-1 bg-slate-100 dark:bg-slate-700/50 rounded-lg">
                                                        <span className="text-sm font-semibold text-slate-700 dark:text-slate-200 truncate" title={page.title}>{page.title}</span>
                                                        <span className={`font-mono text-sm font-bold ${getPointScoreColor(page.pageScore, page.pageMax)} ${getPointScoreTextColor(page.pageScore, page.pageMax)} px-2 py-0.5 rounded`}>
                                                            {page.pageScore}/{page.pageMax}
                                                        </span>
                                                    </div>
                                                    {page.items.map((qs) => {
                                                        const question = questionById.get(String(qs.questionId));
                                                        const qText = question ? (question.text || '').replace(/<[^>]*>/g, '').trim() || `Frage ${qs.questionId}` : `Frage ${qs.questionId}`;
                                                        const qDescription = question ? (question.description || '').replace(/<[^>]*>/g, '').trim() : '';
                                                        const isEditing = editingPointQuestionId === qs.questionId;
                                                        return (
                                                            <div
                                                                key={qs.questionId}
                                                                onClick={() => jumpToAnswer(qs.questionId)}
                                                                title="Zur Antwort springen"
                                                                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700/50 group cursor-pointer"
                                                            >
                                                                <div className="flex items-center gap-2">
                                                                    <span className="flex-1 text-sm text-slate-600 dark:text-slate-400 truncate" title={qText}>
                                                                        {qText.substring(0, 50)}{qText.length > 50 ? '...' : ''}
                                                                    </span>
                                                                    {isEditing ? (
                                                                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                                                            <input
                                                                                type="number"
                                                                                min="0"
                                                                                max={qs.maxScore}
                                                                                value={editingPointValue ?? qs.score}
                                                                                onChange={(e) => setEditingPointValue(e.target.value)}
                                                                                className="w-16 p-1 text-sm text-center border border-amber-300 dark:border-amber-600 rounded bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200"
                                                                                autoFocus
                                                                                onKeyDown={(e) => {
                                                                                    if (e.key === 'Enter') handlePointOverride(qs.questionId, editingPointValue);
                                                                                    if (e.key === 'Escape') { setEditingPointQuestionId(null); setEditingPointValue(null); }
                                                                                }}
                                                                            />
                                                                            <button onClick={() => handlePointOverride(qs.questionId, editingPointValue)} className="p-1 text-green-600 hover:text-green-700">
                                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                                                            </button>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="flex items-center gap-1">
                                                                            {qs.redFlag && (
                                                                                <span className="text-sm" title={qs.reason || 'Als Red Flag eingestuft'}>🚩</span>
                                                                            )}
                                                                            <span className={`font-mono text-sm font-medium ${getPointScoreColor(qs.score, qs.maxScore)} ${getPointScoreTextColor(qs.score, qs.maxScore)} px-1.5 py-0.5 rounded`}>
                                                                                {qs.score}/{qs.maxScore}
                                                                            </span>
                                                                            <button
                                                                                onClick={(e) => { e.stopPropagation(); setEditingPointQuestionId(qs.questionId); setEditingPointValue(qs.score); }}
                                                                                className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity"
                                                                                title="Punkte anpassen"
                                                                            >
                                                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                                </svg>
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                {qDescription && (
                                                                    <p className="mt-0.5 text-xs italic text-slate-400 dark:text-slate-500 leading-relaxed pl-0.5">{qDescription}</p>
                                                                )}
                                                                {qs.reason && (
                                                                    <p className="mt-1 text-xs text-slate-400 dark:text-slate-500 leading-relaxed pl-0.5">{qs.reason}</p>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ))}
                                            {/* Bonus / Malus — read-only here for reconciliation; edit it in the Analyse tab */}
                                            {(Number(pointEvaluation.manualAdjustment) || 0) !== 0 && (() => {
                                                const adjustment = Number(pointEvaluation.manualAdjustment) || 0;
                                                return (
                                                    <div className="flex items-center justify-between gap-2 px-2 py-1">
                                                        <span className="text-sm text-slate-600 dark:text-slate-300">Bonus / Malus</span>
                                                        <span className={`font-mono text-sm font-semibold ${adjustment > 0 ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                                                            {adjustment > 0 ? '+' : ''}{adjustment}
                                                        </span>
                                                    </div>
                                                );
                                            })()}
                                            {/* Gesamt = page subtotals + Bonus/Malus */}
                                            <div className="flex items-center justify-between gap-2 px-2 pt-2 mt-1 border-t border-slate-200 dark:border-slate-600">
                                                <span className="text-sm font-bold text-slate-800 dark:text-slate-100">Gesamt</span>
                                                <span className={`font-mono text-sm font-bold ${getPointScoreColor(pointEvaluation.totalScore, pointEvaluation.maxScore)} ${getPointScoreTextColor(pointEvaluation.totalScore, pointEvaluation.maxScore)} px-2 py-0.5 rounded`}>
                                                    {pointEvaluation.totalScore}/{pointEvaluation.maxScore}
                                                </span>
                                            </div>
                                        </div>
                                        );
                                    })()
                                ) : (
                                    <p className="text-sm text-slate-500 dark:text-slate-400">
                                        Noch keine Punktebewertung durchgeführt. Klicken Sie auf "Bewertung starten" um die Bewertung zu starten.
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Notes Section — feed of posts per account */}
                        <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
                            <h3 className="text-lg sm:text-xl font-bold text-slate-800 dark:text-slate-100 mb-4">Notizen</h3>

                            <div className="space-y-3 mb-4">
                                {noteEntries.length === 0 && (
                                    <p className="text-sm text-slate-500 dark:text-slate-400 italic">Noch keine Notizen vorhanden.</p>
                                )}
                                {noteEntries.map((e) => (
                                    <div key={e.id} className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
                                        {noteByline(e) && (
                                            <div className="text-xs text-slate-400 dark:text-slate-500 mb-1">{noteByline(e)}</div>
                                        )}
                                        {editingId === e.id ? (
                                            <div className="space-y-2">
                                                <textarea value={editingText} onChange={(ev) => setEditingText(ev.target.value)} className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 text-sm min-h-[80px]" />
                                                <div className="flex gap-2">
                                                    <button onClick={handleEditPost} className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">Speichern</button>
                                                    <button onClick={() => { setEditingId(null); setEditingText(''); }} className="px-3 py-1 text-sm bg-slate-500 text-white rounded hover:bg-slate-600">Abbrechen</button>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap leading-relaxed">{e.text}</p>
                                                <div className="flex gap-3 mt-2">
                                                    <button onClick={() => { setEditingId(e.id); setEditingText(e.text); }} className="text-xs text-blue-600 hover:underline">Bearbeiten</button>
                                                    <button onClick={() => handleDeletePost(e.id)} className="text-xs text-red-600 hover:underline">Löschen</button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <div className="space-y-2">
                                <textarea
                                    value={newPost}
                                    onChange={(e) => setNewPost(e.target.value)}
                                    placeholder="Neuen Post hinzufügen…"
                                    className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 resize-vertical min-h-[90px] focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                <button onClick={handleAddPost} disabled={!newPost.trim()} className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50">
                                    Hinzufügen
                                </button>
                            </div>
                        </div>

                        {/* Quick status switch — without the detour over the list */}
                        <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
                            <h3 className="text-lg sm:text-xl font-bold text-slate-800 dark:text-slate-100 mb-4">Status</h3>
                            <StatusQuickActions
                                status={submission.status}
                                submissionId={submission.id}
                                onStatusChange={handleStatusChange}
                            />
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Reevaluation Confirmation Modal */}
            {showReevaluateConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl p-4 sm:p-6 max-w-md w-full mx-4">
                        <h3 className="text-lg sm:text-xl font-bold text-slate-800 dark:text-slate-100 mb-4">Bewertung neu durchführen?</h3>
                        <div className="space-y-3 text-sm sm:text-base text-slate-600 dark:text-slate-300">
                            <p>⚠️ <strong>Achtung:</strong> Die aktuelle Bewertung wird überschrieben und kann nicht wiederhergestellt werden.</p>
                            <p>Die KI wird eine neue Gesamtbewertung (Analyse, Qualifikation und Punktevergabe) basierend auf den aktuellen Bewertungskriterien durchführen.</p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3 mt-6">
                            <button 
                                onClick={cancelReevaluate}
                                className="flex-1 px-4 py-2 text-slate-600 dark:text-slate-400 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                            >
                                Abbrechen
                            </button>
                            <button 
                                onClick={confirmReevaluate}
                                className="flex-1 px-4 py-2 bg-yellow-600 text-white font-semibold rounded-lg hover:bg-yellow-700 transition-colors"
                            >
                                Neu bewerten
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showSendAssessment && (
                <SendAssessmentDialog
                    key={submission.id}
                    submission={submission}
                    onClose={() => setShowSendAssessment(false)}
                    onSetUpJob={onSetUpJob}
                    onSent={() => {
                        setShowSendAssessment(false);
                        loadInvites();
                        onInvitationSent?.(submission.id);
                    }}
                />
            )}
        </div>
    );
}
